import {matchesLocalizedSearch} from '../utils/localizedSearch';
import {t, useLanguage} from '../i18n';
import {readSearchMarket,matchesMarket,marketCacheKey} from "../utils/market";
import MarketplaceSearch from "../components/MarketplaceSearch";
import "../styles/searchExperience.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../firebase";
import fsClient from "../data/firestoreClient";
import { isIosCapacitor } from "../utils/nativeHttp";
import BusinessResultCard from "../components/SearchBusinessCard";

// -------------------------------
// LOCAL ICONS (same as MainMenu desired)
// Ajusta los imports/nombres para que coincidan con tus archivos reales.
// Carpeta: src/assets/images/servicios
// -------------------------------
import bellezaBarberiasIcon from "../assets/images/Servicios/servicio1.png";
import bienestarSpaIcon from "../assets/images/Servicios/servicio2.png";
import entretenimientoFitnessIcon from "../assets/images/Servicios/servicio4.png";
import otrosIcon from "../assets/images/Servicios/servicio5.png";
import eventosIcon from "../assets/images/Servicios/servicio3.png";

// Map por ID (preferido) y/o por nombre normalizado
const LOCAL_SERVICE_ICONS = {
  // IDs (si tus docs en serviceCategories tienen estos ids)
  belleza_barberias: bellezaBarberiasIcon,
  bienestar_spa: bienestarSpaIcon,
  entretenimiento_fitness: entretenimientoFitnessIcon,
  otros: otrosIcon,
  eventos: eventosIcon,

  // Nombres normalizados (fallback si el id no matchea)
  belleza__barberias: bellezaBarberiasIcon,
  bienestar__spa: bienestarSpaIcon,
  entretenimiento__fitness: entretenimientoFitnessIcon,
};


const SERVICE_DISPLAY_ORDER = [
  "belleza_barberias",
  "bienestar_spa",
  "eventos",
  "entretenimiento_fitness",
  "otros",
];

function serviceOrderKey(cat) {
  const title = cat?.name || cat?.title || cat?.id || "";
  const idKey = normalizeName(cat?.id || "");
  const titleKey = normalizeName(title);

  if (idKey.includes("belleza") || idKey.includes("barber") || titleKey.includes("belleza") || titleKey.includes("barber")) return "belleza_barberias";
  if (idKey.includes("bienestar") || idKey.includes("spa") || titleKey.includes("bienestar") || titleKey.includes("spa")) return "bienestar_spa";
  if (idKey.includes("evento") || titleKey.includes("evento")) return "eventos";
  if (idKey.includes("fitness") || idKey.includes("entreten") || titleKey.includes("fitness") || titleKey.includes("entreten")) return "entretenimiento_fitness";
  if (idKey.includes("otro") || titleKey.includes("otro")) return "otros";
  return idKey || titleKey;
}

function sortServiceCategoriesLikeMainMenu(cats) {
  return [...(Array.isArray(cats) ? cats : [])].sort((a, b) => {
    const ak = serviceOrderKey(a);
    const bk = serviceOrderKey(b);
    const ai = SERVICE_DISPLAY_ORDER.indexOf(ak);
    const bi = SERVICE_DISPLAY_ORDER.indexOf(bk);
    if (ai !== -1 || bi !== -1) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    return (Number(a?.order) || 0) - (Number(b?.order) || 0);
  });
}

function isEventsCategory(catOrId) {
  const id = typeof catOrId === "object" ? catOrId?.id : catOrId;
  const title = typeof catOrId === "object" ? (catOrId?.name || catOrId?.title || "") : "";
  const text = `${id || ""} ${title || ""}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return text.includes("evento") || text.includes("event");
}

function getEarlyAccessPath() {
  return "/early-access";
}


const PAGE_SIZE = 12;
const CACHE_TTL_MS = 1000 * 60 * 3;

function normalizeName(s) {
  if (!s) return "";
  return String(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/gi, "_")
    .toLowerCase();
}

function looksLikeFirestoreId(value) {
  const raw = String(value || "").trim();
  if (!raw) return false;

  // Firestore auto IDs usually look like random alphanumeric strings.
  // Do not ever show these as customer-facing titles.
  const compact = /^[A-Za-z0-9_-]{16,}$/.test(raw);
  const hasHumanSeparators = /[\sáéíóúñÁÉÍÓÚÑ]/.test(raw);
  const hasKnownWords = /belleza|barber|bienestar|spa|evento|fitness|entreten|otro|beauty|wellness|event|other/i.test(raw);

  return compact && !hasHumanSeparators && !hasKnownWords;
}

function getFriendlyServiceTitle(value, fallback = "Servicios") {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  if (looksLikeFirestoreId(raw)) return fallback;

  const key = normalizeName(raw);

  if (!key) return fallback;
  if (key.includes("belleza") || key.includes("barber") || key.includes("beauty")) return "Belleza & Barberías";
  if (key.includes("bienestar") || key.includes("spa") || key.includes("wellness")) return "Bienestar & Spa";
  if (key.includes("evento") || key.includes("event")) return "Eventos";
  if (key.includes("entreten") || key.includes("fitness") || key.includes("gym") || key.includes("entertain")) return "Entretenimiento & Fitness";
  if (key.includes("otro") || key.includes("other")) return "Otros";

  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase()) || fallback;
}


function norm(s) {
  return (s ?? "").toString().trim().toLowerCase();
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.ts) return null;
    if (Date.now() - parsed.ts > (parsed.ttl || CACHE_TTL_MS)) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.data || null;
  } catch {
    return null;
  }
}

function writeCache(key, data, ttl = CACHE_TTL_MS) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), ttl, data }));
  } catch {}
}


function isPublicBusiness(biz) {
  if (!biz || typeof biz !== "object") return false;
  return (
    biz.visible !== false &&
    biz.hidden !== true &&
    biz.isActive !== false &&
    biz.published !== false
  );
}

function sanitizeBusinesses(list) {
  if (!Array.isArray(list)) return [];
  return list.filter((b) => b && typeof b === "object" && isPublicBusiness(b) && matchesMarket(b));
}

function matchBusinessToAnyKey(b, keys) {
  if (!b || typeof b !== "object") return false;
  const needles = (Array.isArray(keys) ? keys : []).map(norm).filter(Boolean);
  if (needles.length === 0) return false;

  const services = Array.isArray(b.services) ? b.services : [];
  for (const s of services) {
    if (!s) continue;
    if (typeof s === "string") {
      const v = norm(s);
      if (needles.some((k) => v === k || v.includes(k) || k.includes(v))) return true;
    } else if (typeof s === "object") {
      const v = norm(s.name || s.title || s.id || s.key);
      if (v && needles.some((k) => v === k || v.includes(k) || k.includes(v))) return true;
    }
  }

  const typeVal = norm(b.type);
  if (typeVal && needles.some((k) => typeVal === k || typeVal.includes(k) || k.includes(typeVal))) return true;

  const cats = Array.isArray(b.categories) ? b.categories : [];
  for (const c of cats) {
    const v = norm(c);
    if (v && needles.some((k) => v === k || v.includes(k) || k.includes(v))) return true;
  }

  return false;
}

function useOnEscape(onEsc, enabled = true) {
  React.useEffect(() => {
    if (!enabled) return undefined;
    function onKeyDown(e) {
      if (e.key === "Escape") onEsc && onEsc();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onEsc, enabled]);
}

// --- Portal Dropdown ---
function DropdownPortal({ open, anchorEl, onClose, children, width = 320 }) {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (!anchorEl) return;

    const update = () => {
      try {
        const r = anchorEl.getBoundingClientRect();
        const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12));
        const top = r.bottom + 10;

        setPos({
          left,
          top,
          width,
          arrowLeft: Math.min(width - 24, Math.max(18, r.left - left + 18)),
        });
      } catch {}
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorEl, width]);

  useEffect(() => {
    if (!open) return;

    function onMouseDown(e) {
      const dd = document.getElementById("__loop_dropdown_portal__");
      const clickedInside = dd && dd.contains(e.target);
      const clickedAnchor = anchorEl && anchorEl.contains && anchorEl.contains(e.target);
      if (!clickedInside && !clickedAnchor) onClose && onClose();
    }

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open, onClose, anchorEl]);

  if (!open || !pos) return null;

  return ReactDOM.createPortal(
    <div
      id="__loop_dropdown_portal__"
      style={{ position: "fixed", inset: 0, zIndex: 999999, pointerEvents: "none" }}
      aria-hidden="true"
    >
      <div
        style={{ position: "absolute", left: pos.left, top: pos.top, width: pos.width, pointerEvents: "auto" }}
        role="dialog"
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: -8,
            left: pos.arrowLeft,
            width: 14,
            height: 14,
            transform: "rotate(45deg)",
            background: "#fff",
            borderLeft: "1px solid rgba(15,23,42,0.12)",
            borderTop: "1px solid rgba(15,23,42,0.12)",
          }}
        />
        <div
          style={{
            background: "#fff",
            border: "1px solid rgba(15,23,42,0.12)",
            borderRadius: 22,
            boxShadow: "0 22px 60px rgba(15,23,42,0.18)",
            padding: 16,
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

function ControlButton({ children, onClick, active = false, ariaLabel, btnRef }) {
  return (
    <button
      ref={btnRef}
      type="button"
      aria-label={ariaLabel}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick && onClick();
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 15px",
        borderRadius: 999,
        border: active ? "1px solid #99f6e4" : "1px solid #e2e8f0",
        background: active ? "#ecfdf5" : "#fff",
        boxShadow: active ? "0 10px 24px rgba(15,118,110,0.10)" : "0 8px 20px rgba(15,23,42,0.055)",
        cursor: "pointer",
        fontSize: 14,
        lineHeight: "20px",
        userSelect: "none",
      }}
    >
      {children}
      <span style={{ fontSize: 12, opacity: 0.7 }}>▾</span>
    </button>
  );
}

function CheckboxRow({ checked, onChange, title, icon }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 4px",
        cursor: "pointer",
        userSelect: "none",
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
        <span
          aria-hidden="true"
          style={{
            width: 26,
            height: 26,
            borderRadius: 8,
            background: "#f1f5f9",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#0f766e",
            fontSize: 14,
          }}
        >
          {icon}
        </span>
        <span style={{ fontSize: 14, color: "#0f172a" }}>{title}</span>
      </span>

      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
      />
    </label>
  );
}

function RadioRow({ checked, onChange, title }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 4px",
        cursor: "pointer",
        userSelect: "none",
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <span style={{ fontSize: 14, color: "#0f172a" }}>{title}</span>
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
      />
    </label>
  );
}

// fallback (if local icon not found)
function pickCategoryIconFromDoc(cat) {
  if (!cat) return null;
  const candidates = [
    cat.iconUrl,
    cat.icon,
    cat.imageUrl,
    cat.image,
    cat.iconPath,
    cat.iconSrc,
    cat.icon_url,
    cat.photoUrl,
    cat.picture,
    cat.thumbnail,
    cat.logoUrl,
    cat.logo,
    cat.menuImage,
  ];
  for (const c of candidates) {
    if (!c) continue;
    if (typeof c === "string" && c.trim()) return c.trim();
    if (c && typeof c === "object" && typeof c.url === "string" && c.url.trim()) return c.url.trim();
  }
  return null;
}

function buildCategoryIconCandidates(url) {
  const PUBLIC_FALLBACK = (process.env.PUBLIC_URL || "") + "/no-image.png";
  if (!url || typeof url !== "string") return [PUBLIC_FALLBACK];

  const trimmed = url.trim();
  const out = [trimmed];

  try {
    if (trimmed.startsWith("/") && typeof window !== "undefined" && window.location?.origin) {
      out.push(window.location.origin + trimmed);
    }
  } catch {}

  try {
    const pub = process.env.PUBLIC_URL || "";
    if (pub) {
      const stripped = trimmed.replace(/^\/+/, "");
      out.push((pub.endsWith("/") ? pub.slice(0, -1) : pub) + "/" + stripped);
    }
  } catch {}

  out.push(PUBLIC_FALLBACK);
  return Array.from(new Set(out.filter(Boolean)));
}


function categoryHeroCopy(title) {
  const t = String(title || "").toLowerCase();
  if (t.includes("belleza") || t.includes("barber")) {
    return "Encuentra salones, barberías y especialistas cerca de ti. Compara opciones y reserva en pocos pasos.";
  }
  if (t.includes("bienestar") || t.includes("spa")) {
    return "Explora spas, masajes, faciales y servicios de bienestar con una experiencia simple y rápida.";
  }
  if (t.includes("fitness") || t.includes("entreten")) {
    return "Descubre clases, entrenadores y experiencias activas disponibles para reservar.";
  }
  if (t.includes("otros")) {
    return "Explora negocios disponibles en Loop y encuentra el servicio que necesitas.";
  }
  return "Explora negocios disponibles, compara servicios y reserva con una experiencia limpia y rápida.";
}

function marketplaceHeroCopy({ title, isSearchMode, searchQuery }) {
  if (isSearchMode && searchQuery) {
    return `Encuentra opciones disponibles en Loop, compara rápido y reserva sin salir de la experiencia.`;
  }
  return categoryHeroCopy(title);
}

function businessTextForMarketplace(biz) {
  if (!biz || typeof biz !== "object") return "";
  const parts = [
    biz.name,
    biz.businessName,
    biz.title,
    biz.type,
    biz.category,
    biz.businessType,
    biz.businessTypeLabel,
    biz.businessSubtype,
    biz.businessSubtypeLabel,
    biz.description,
    biz.shortDescription,
    biz.about,
    biz.address,
    biz.city,
    biz.location,
    biz.coverageArea,
    biz.serviceMode,
  ];
  const serviceParts = Array.isArray(biz.services)
    ? biz.services.map((service) => {
        if (!service) return "";
        if (typeof service === "string") return service;
        return [service.name, service.title, service.label, service.id, service.category, service.templateKey].filter(Boolean).join(" ");
      })
    : [];
  const areaParts = Array.isArray(biz.serviceAreas)
    ? biz.serviceAreas.map((area) => typeof area === "string" ? area : [area.label, area.zone, area.corregimiento, area.district, area.province].filter(Boolean).join(" "))
    : [];
  const advancedAreaParts = Array.isArray(biz.serviceAreasAdvanced)
    ? biz.serviceAreasAdvanced.map((area) => [area.label, area.zone, area.corregimiento, area.district, area.province].filter(Boolean).join(" "))
    : [];
  return [...parts, ...serviceParts, ...areaParts, ...advancedAreaParts].filter(Boolean).join(" ");
}

function matchBusinessToSearchQuery(biz, queryValue) {
  const q = normalizeName(queryValue);
  if (!q) return true;
  const text = normalizeName(businessTextForMarketplace(biz));
  if (!text) return false;
  const tokens = q.split("_").filter((token) => token.length > 1);
  if (text.includes(q)) return true;
  if (tokens.length === 0) return false;
  return tokens.every((token) => text.includes(token)) || matchesLocalizedSearch(text,queryValue);
}

function serviceTextForMarketplace(service) {
  if (!service || typeof service !== "object") return "";
  const parts = [
    service.name,
    service.title,
    service.label,
    service.description,
    service.shortDescription,
    service.category,
    service.categoryLabel,
    service.type,
    service.templateKey,
    service.serviceIconKey,
    service.iconKey,
    service.publicPriceLabel,
    service.priceLabel,
  ];

  const characters = Array.isArray(service.characters)
    ? service.characters.map((item) => typeof item === "string" ? item : [item?.name, item?.label, item?.title].filter(Boolean).join(" "))
    : [];

  const extras = Array.isArray(service.extras || service.optionalExtras)
    ? (service.extras || service.optionalExtras).map((item) => typeof item === "string" ? item : [item?.name, item?.label, item?.title].filter(Boolean).join(" "))
    : [];

  return [...parts, ...characters, ...extras].filter(Boolean).join(" ");
}

function isPublicSearchableService(service) {
  if (!service || typeof service !== "object") return false;
  const status = String(service.status || service.serviceStatus || "").toLowerCase();
  return (
    service.visible !== false &&
    service.hidden !== true &&
    service.isActive !== false &&
    service.active !== false &&
    service.published !== false &&
    !["deleted", "archived", "draft", "inactive", "pausado", "paused"].includes(status)
  );
}

function matchServiceToSearchQuery(service, queryValue) {
  const q = normalizeName(queryValue);
  if (!q) return false;
  if (!isPublicSearchableService(service)) return false;
  const text = normalizeName(serviceTextForMarketplace(service));
  if (!text) return false;
  const tokens = q.split("_").filter((token) => token.length > 1);
  if (text.includes(q)) return true;
  if (tokens.length === 0) return false;
  return tokens.every((token) => text.includes(token)) || matchesLocalizedSearch(text,queryValue);
}

function getServiceBusinessId(service, fallback = "") {
  return String(
    service?.businessId ||
    service?.businessID ||
    service?.ownerBusinessId ||
    service?.bizId ||
    service?.business ||
    fallback ||
    ""
  ).trim();
}

function uniqueServicesById(list = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(list) ? list : []).forEach((service, idx) => {
    if (!service) return;
    const key = String(service.id || service.serviceId || service.name || service.title || idx);
    const normKey = normalizeName(key);
    if (seen.has(normKey)) return;
    seen.add(normKey);
    out.push(service);
  });
  return out;
}

function mergeServiceMatchesIntoBusinesses(baseBusinesses = [], serviceMatchesByBusiness = new Map()) {
  const byId = new Map();
  (Array.isArray(baseBusinesses) ? baseBusinesses : []).forEach((biz) => {
    if (!biz?.id) return;
    byId.set(String(biz.id), { ...biz });
  });

  serviceMatchesByBusiness.forEach((services, businessId) => {
    const id = String(businessId || "");
    if (!id || !byId.has(id)) return;

    const biz = byId.get(id);
    const existing = Array.isArray(biz.services) ? biz.services : [];
    const matched = uniqueServicesById(services).map((service) => ({
      ...service,
      __matchedBySearch: true,
    }));

    byId.set(id, {
      ...biz,
      services: uniqueServicesById([...existing, ...matched]),
      __matchedServices: matched,
      __matchedServiceNames: matched.map((service) => service.name || service.title || service.label).filter(Boolean),
      __matchedByServiceSearch: matched.length > 0,
    });
  });

  return Array.from(byId.values());
}

async function findMatchingChildServicesForBusinesses({ db, businesses = [], queryValue, useNativeRest = false }) {
  const q = normalizeName(queryValue);
  const serviceMatchesByBusiness = new Map();
  if (!q || !Array.isArray(businesses) || businesses.length === 0) return serviceMatchesByBusiness;

  const publicBusinessIds = new Set(businesses.map((biz) => String(biz?.id || "")).filter(Boolean));

  const addMatch = (businessId, service) => {
    const id = String(businessId || "");
    if (!id || !publicBusinessIds.has(id)) return;
    if (!matchServiceToSearchQuery(service, queryValue)) return;
    const current = serviceMatchesByBusiness.get(id) || [];
    current.push(service);
    serviceMatchesByBusiness.set(id, current);
  };

  if (useNativeRest) {
    await Promise.all(
      businesses.map(async (biz) => {
        const businessId = String(biz?.id || "");
        if (!businessId) return;
        try {
          const rows = await fsClient.listCollectionByPath(`businesses/${businessId}/services`, {}, { db });
          (Array.isArray(rows) ? rows : []).forEach((row) => {
            addMatch(businessId, { id: row?.id, ...(row || {}), businessId });
          });
        } catch (_) {}
      })
    );
    return serviceMatchesByBusiness;
  }

  try {
    const snap = await getDocs(query(collectionGroup(db, "services"), limit(1500)));
    snap.forEach((d) => {
      const parentBusinessId = d?.ref?.parent?.parent?.id || "";
      const data = { id: d.id, ...(d.data() || {}), businessId: getServiceBusinessId(d.data() || {}, parentBusinessId) };
      addMatch(data.businessId || parentBusinessId, data);
    });
    return serviceMatchesByBusiness;
  } catch (err) {
    try { console.warn("[SR] collectionGroup services search failed; using per-business fallback", err); } catch (_) {}
  }

  await Promise.all(
    businesses.map(async (biz) => {
      const businessId = String(biz?.id || "");
      if (!businessId) return;
      try {
        const snap = await getDocs(collection(db, `businesses/${businessId}/services`));
        snap.forEach((d) => addMatch(businessId, { id: d.id, ...(d.data() || {}), businessId }));
      } catch (_) {}
    })
  );

  return serviceMatchesByBusiness;
}

function businessSupportsHomeService(biz) {
  const mode = String(biz?.serviceMode || biz?.mode || biz?.visitMode || "").toLowerCase();
  return Boolean(
    mode.includes("delivery") ||
    mode.includes("domicilio") ||
    mode.includes("hybrid") ||
    mode.includes("mixto") ||
    biz?.homeService === true ||
    biz?.offers?.home === true ||
    (Array.isArray(biz?.serviceAreas) && biz.serviceAreas.length > 0) ||
    (Array.isArray(biz?.serviceAreasAdvanced) && biz.serviceAreasAdvanced.length > 0)
  );
}

function businessOpenOrAvailableToday(biz) {
  return Boolean(biz?.availableToday || biz?.isOpen || biz?.openNow || biz?.acceptsToday || biz?.calendarSettings?.acceptsSameDay !== false);
}

function businessSupportsOnsiteService(biz) {
  const mode = String(biz?.serviceMode || biz?.mode || biz?.visitMode || "").toLowerCase();
  return !mode || mode.includes("onsite") || mode.includes("local") || mode.includes("hybrid") || mode.includes("mixto");
}

function businessRatingValue(biz) {
  const value = Number(biz?.rating || biz?.averageRating || biz?.stars || 0);
  return Number.isFinite(value) ? value : 0;
}

function businessUpdatedTime(biz) {
  const raw = biz?.updatedAt || biz?.createdAt || biz?.publishedAt || null;
  try {
    if (raw && typeof raw.toMillis === "function") return raw.toMillis();
    if (raw && raw.seconds) return Number(raw.seconds) * 1000;
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function businessLocationLabelForResult(biz) {
  const candidates = [biz?.city, biz?.cityName, biz?.locationCity, biz?.address, biz?.locationLabel, biz?.coverageArea];
  for (const candidate of candidates) {
    const clean = String(candidate || "").trim();
    if (clean) return clean;
  }
  return "Panamá";
}

/**
 * UPDATED ServiceTile:
 * - Make the square be FULL image (no 52x52 inner icon).
 * - Uses objectFit:"cover" to fill the entire 86x86 box.
 */

function getSmartRelatedChips({ title, queryValue, isSearchMode }) {
  const raw = `${title || ""} ${queryValue || ""}`.toLowerCase();
  if (raw.includes("barber") || raw.includes("belleza") || raw.includes("uñas") || raw.includes("unas")) {
    return ["Uñas", "Corte de cabello", "Cejas", "Belleza & Barberías", "San Francisco"];
  }
  if (raw.includes("spa") || raw.includes("bienestar") || raw.includes("masaje")) {
    return ["Masajes", "Faciales", "Spa", "Bienestar & Spa", "Costa del Este"];
  }
  if (raw.includes("evento") || raw.includes("infantil") || raw.includes("fotografia") || raw.includes("fotografía")) {
    return ["Eventos infantiles", "Fotografía", "Personajes", "Shows", "Todos los eventos"];
  }
  if (raw.includes("fitness") || raw.includes("entreten")) {
    return ["Fitness", "Clases", "Entrenadores", "Entretenimiento & Fitness", "Panamá"];
  }
  return isSearchMode
    ? ["Belleza & Barberías", "Uñas", "Masajes", "Eventos infantiles", "Costa del Este"]
    : ["Ver todos", "Belleza & Barberías", "Bienestar & Spa", "A domicilio", "Disponible hoy"];
}

function SmartResultsEmptyState({
  title,
  searchQuery,
  hasBaseResults,
  activeFilterCount,
  relatedChips = [],
  onClearFilters,
  onExploreAll,
  onEarlyAccess,
  onChip,
}) {
  const filterEmpty = hasBaseResults && activeFilterCount > 0;
  const headline = filterEmpty
    ? "No encontramos resultados con esos filtros."
    : searchQuery
      ? t('No encontramos “{query}” todavía.',{query:searchQuery})
      : t('Todavía no hay negocios publicados en {category}.',{category:title || t('esta categoría')});
  const copy = filterEmpty
    ? "Prueba quitando algún filtro o cambia el orden para ver más opciones disponibles."
    : "Puedes probar una búsqueda relacionada, explorar categorías populares o solicitar acceso para que un negocio se una a Loop.";

  return (
    <div className="sr-smart-empty" role="status">
      <div className="sr-smart-empty-glow" aria-hidden="true"><span>⌕</span></div>
      <div className="sr-smart-empty-copy">
        <div className="sr-smart-empty-kicker">{t("Recomendaciones inteligentes")}</div>
        <h3>{headline}</h3>
        <p>{copy}</p>
      </div>

      {relatedChips.length ? (
        <div className="sr-smart-empty-chips" aria-label={t("Búsquedas sugeridas")}>
          {relatedChips.slice(0, 6).map((chip) => (
            <button key={chip} type="button" onClick={() => onChip && onChip(chip)}>{chip}</button>
          ))}
        </div>
      ) : null}

      <div className="sr-smart-empty-actions">
        {filterEmpty ? (
          <button type="button" className="primary" onClick={onClearFilters}>{t("Limpiar filtros")}</button>
        ) : (
          <button type="button" className="primary" onClick={onExploreAll}>{t("Explorar marketplace")}</button>
        )}
        <button type="button" className="secondary" onClick={onEarlyAccess}>{t("Sumar mi negocio")}</button>
      </div>
    </div>
  );
}

function ServiceTile({ active, title, iconUrl, iconCandidates = [], onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`sr-service-tile ${active ? "is-active" : ""}`}
      aria-pressed={active}
    >
      <div className="sr-service-img" aria-hidden="true">
        {iconUrl ? (
          <img
            src={iconUrl}
            alt=""
            onError={(e) => {
              try {
                const cur = e.currentTarget.src;
                const next = (iconCandidates || []).find((c) => c && c !== cur);
                if (next) e.currentTarget.src = next;
              } catch {}
            }}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", background: "#b8dedd" }} />
        )}
      </div>
      <div className="sr-service-title">{title}</div>
    </button>
  );
}

export default function ServiceResultsPage() {
  useLanguage();
  const { serviceKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const searchQueryParam = (searchParams.get("q") || searchParams.get("query") || "").trim();
  const locationQueryParam = (searchParams.get("loc") || "").trim();
  const isSearchMode = String(serviceKey || "").toLowerCase() === "search" || Boolean(searchQueryParam);

  const paramRaw = isSearchMode ? (searchQueryParam || (serviceKey ? decodeURIComponent(serviceKey) : "")) : (serviceKey ? decodeURIComponent(serviceKey) : "");
  const paramNorm = normalizeName(paramRaw);

  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [error, setError] = useState(null);

  const [hasMore, setHasMore] = useState(false);
  const lastVisibleRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const savedSearchKey = `loop_results:${location.pathname}:${location.search}`;
  const [savedSearch] = useState(() => { try { return JSON.parse(sessionStorage.getItem(savedSearchKey) || "{}"); } catch (_) { return {}; } });
  const [sortBy, setSortBy] = useState(savedSearch.sortBy || "recommended");
  const [filters, setFilters] = useState(savedSearch.filters || { online: false, home: false, onsite: false, offer: false, today: false, rated: false });
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [filterTab, setFilterTab] = useState("keyword");
  const [selectedSubservice, setSelectedSubservice] = useState(savedSearch.selectedSubservice || null);
  const [selectedLocation, setSelectedLocation] = useState(locationQueryParam || savedSearch.selectedLocation || null);
  const [locationFilterSearch, setLocationFilterSearch] = useState("");
  const [localSearch, setLocalSearch] = useState(searchQueryParam || "");

  useEffect(() => {
    setLocalSearch(searchQueryParam || (isSearchMode ? paramRaw : ""));
  }, [searchQueryParam, isSearchMode, paramRaw]);

  useEffect(() => {
    setSelectedLocation(locationQueryParam || savedSearch.selectedLocation || null);
  }, [locationQueryParam]);

  useEffect(() => {
    try { sessionStorage.setItem(savedSearchKey, JSON.stringify({ sortBy, filters, selectedSubservice, selectedLocation })); } catch (_) {}
  }, [savedSearchKey, sortBy, filters, selectedSubservice, selectedLocation]);

  // Service tiles
  const [serviceCats, setServiceCats] = useState([]);
  const [catsLoading, setCatsLoading] = useState(true);

  // Dropdowns
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const filterBtnRef = useRef(null);
  const sortBtnRef = useRef(null);
  const serviceBtnRef = useRef(null);
  const locationBtnRef = useRef(null);

  useOnEscape(() => {
    setFilterOpen(false);
    setSortOpen(false);
    setServiceOpen(false);
    setLocationOpen(false);
    setFilterSheetOpen(false);
  }, filterOpen || sortOpen || serviceOpen || locationOpen || filterSheetOpen);

  useEffect(() => {
    if (!["keyword", "category", "service", "location", "modality"].includes(filterTab)) {
      setFilterTab("keyword");
    }
  }, [filterTab]);

  const cacheKey = `sr:market1:${marketCacheKey()}:${isSearchMode ? "search" : "category"}:${paramRaw || paramNorm}:${locationQueryParam || "all"}`;

  useEffect(() => {
    let cancelled = false;
    setCatsLoading(true);

    (async () => {
      try {
        let cats = [];
        if (isIosCapacitor()) {
          const list = await fsClient.listCollectionByPath("serviceCategories", {}, { db });
          cats = Array.isArray(list) ? list.map((d) => ({ id: d.id, ...(d || {}) })) : [];
        } else {
          const col = collection(db, "serviceCategories");
          const snap = await getDocs(col);
          cats = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        }
        cats = sortServiceCategoriesLikeMainMenu(cats);
        if (!cancelled) setServiceCats(cats);
      } catch (e) {
        console.warn("[SR] load serviceCategories failed:", e);
        if (!cancelled) setServiceCats([]);
      } finally {
        if (!cancelled) setCatsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  async function getCategoryDocById(id) {
    try {
      if (isIosCapacitor()) {
        const d = await fsClient.getDocByPath(`serviceCategories/${id}`, { db });
        if (d) return { id: d.id || id, data: d || {} };
      } else {
        const snap = await getDoc(doc(db, "serviceCategories", id));
        if (snap && snap.exists()) return { id: snap.id, data: snap.data() || {} };
      }
    } catch {}
    return null;
  }

  async function resolveCategory(param) {
    if (!param) return null;

    const byId = await getCategoryDocById(param);
    if (byId) return byId;

    try {
      if (!isIosCapacitor()) {
        const col = collection(db, "serviceCategories");
        const q1 = query(col, where("name", "==", param));
        const sq1 = await getDocs(q1);
        if (!sq1.empty) return { id: sq1.docs[0].id, data: sq1.docs[0].data() || {} };

        const q2 = query(col, where("nameLower", "==", (param || "").toString().toLowerCase()));
        const sq2 = await getDocs(q2);
        if (!sq2.empty) return { id: sq2.docs[0].id, data: sq2.docs[0].data() || {} };
      }
    } catch {}

    try {
      let allCats = [];
      if (isIosCapacitor()) {
        allCats = await fsClient.listCollectionByPath("serviceCategories", {}, { db });
      } else {
        const col = collection(db, "serviceCategories");
        const all = await getDocs(col);
        allCats = all.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      }
      for (const d of allCats || []) {
        const data = d.data || d || {};
        const id = d.id || null;
        const name = (data.name || "").toString();
        if (!name && !id) continue;
        if (
          String(id || "") === String(param) ||
          name === param ||
          name.toLowerCase() === (param || "").toString().toLowerCase() ||
          normalizeName(name) === paramNorm
        ) {
          return { id: id || param, data };
        }
      }
    } catch {}

    return null;
  }

  const searchRequest = useRef(0);
  useEffect(() => () => { searchRequest.current += 1; }, [paramRaw]);
  async function loadFirstPage(param, pageSize = PAGE_SIZE) {
    const request = ++searchRequest.current;
    if (request !== searchRequest.current) return;
    setError(null);

    try {
      const category = await resolveCategory(param);
      const categoryName = category?.data?.name ? String(category.data.name) : "";
      const categorySlug = normalizeName(categoryName);

      const keysToMatch = Array.from(
        new Set([paramRaw, paramNorm, category?.id, categoryName, categorySlug].filter(Boolean))
      );

      if (isSearchMode) {
        if (isIosCapacitor()) {
          const allBusinesses = await fsClient.listCollectionByPath("businesses", {where:readSearchMarket().code === "PA" ? [] : [{field:"countryCode",op:"EQUAL",value:readSearchMarket().code}]}, { db });
          const all = sanitizeBusinesses(Array.isArray(allBusinesses) ? allBusinesses.map((b) => ({ id: b.id, ...(b || {}) })) : []);
          const directMatches = all.filter((b) => matchBusinessToSearchQuery(b, paramRaw));
          const serviceMatchesByBusiness = await findMatchingChildServicesForBusinesses({ db, businesses: all, queryValue: paramRaw, useNativeRest: true });
          const serviceMatchedIds = new Set(Array.from(serviceMatchesByBusiness.keys()).map(String));
          const combinedBase = all.filter((b) => directMatches.some((m) => String(m.id) === String(b.id)) || serviceMatchedIds.has(String(b.id)));
          const found = mergeServiceMatchesIntoBusinesses(combinedBase, serviceMatchesByBusiness);
          if (request !== searchRequest.current) return;
          setBusinesses(found);
          setHasMore(false);
          lastVisibleRef.current = null;
          writeCache(cacheKey, { list: found, lastLoaded: Date.now() });
          return;
        }

        const col = query(collection(db, "businesses"), ...(readSearchMarket().code === "PA" ? [] : [where("countryCode", "==", readSearchMarket().code)]));
        const snapAll = await getDocs(query(col, limit(1000)));
        const all = sanitizeBusinesses(snapAll.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
        const directMatches = all.filter((b) => matchBusinessToSearchQuery(b, paramRaw));
        const serviceMatchesByBusiness = await findMatchingChildServicesForBusinesses({ db, businesses: all, queryValue: paramRaw });
        const serviceMatchedIds = new Set(Array.from(serviceMatchesByBusiness.keys()).map(String));
        const combinedBase = all.filter((b) => directMatches.some((m) => String(m.id) === String(b.id)) || serviceMatchedIds.has(String(b.id)));
        const found = mergeServiceMatchesIntoBusinesses(combinedBase, serviceMatchesByBusiness);
        if (request !== searchRequest.current) return;
        setBusinesses(found);
        setHasMore(false);
        lastVisibleRef.current = null;
        writeCache(cacheKey, { list: found, lastLoaded: Date.now() });
        return;
      }

      if (isIosCapacitor()) {
        const allBusinesses = await fsClient.listCollectionByPath("businesses", {where:readSearchMarket().code === "PA" ? [] : [{field:"countryCode",op:"EQUAL",value:readSearchMarket().code}]}, { db });
        const all = sanitizeBusinesses(Array.isArray(allBusinesses) ? allBusinesses.map((b) => ({ id: b.id, ...(b || {}) })) : []);
        const found = all.filter((b) => matchBusinessToAnyKey(b, keysToMatch));
        if (request !== searchRequest.current) return;
        setBusinesses(found);
        setHasMore(false);
        lastVisibleRef.current = null;
        writeCache(cacheKey, { list: found, lastLoaded: Date.now() });
        return;
      }

      const col = query(collection(db, "businesses"), ...(readSearchMarket().code === "PA" ? [] : [where("countryCode", "==", readSearchMarket().code)]));
      let snapshot = null;

      if (category?.id) {
        try {
          const q0 = query(
            col,
            where("status", "==", "active"),
            where("type", "==", category.id),
            orderBy("updatedAt", "desc"),
            limit(pageSize)
          );
          snapshot = await getDocs(q0);
        } catch {
          snapshot = null;
        }

        if (!snapshot || snapshot.empty) {
          try {
            const q1 = query(col, where("type", "==", category.id), limit(pageSize));
            snapshot = await getDocs(q1);
          } catch {
            snapshot = null;
          }
        }
      }

      const candidatesForArrayContains = [paramRaw, categoryName, categorySlug].filter(Boolean);
      if ((!snapshot || snapshot.empty) && candidatesForArrayContains.length > 0) {
        for (const k of candidatesForArrayContains) {
          try {
            const q2 = query(col, where("services", "array-contains", k), limit(pageSize));
            const snap = await getDocs(q2);
            if (!snap.empty) {
              snapshot = snap;
              break;
            }
          } catch {}
        }
      }

      if (snapshot && !snapshot.empty) {
        const arr = sanitizeBusinesses(snapshot.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
        lastVisibleRef.current = snapshot.docs[snapshot.docs.length - 1] || null;
        if (request !== searchRequest.current) return;
        setBusinesses(arr);
        setHasMore(snapshot.docs.length === pageSize);
        writeCache(cacheKey, { list: arr, lastLoaded: Date.now() });
        return;
      }

      try {
        const snapAll = await getDocs(query(col, limit(1000)));
        const all = sanitizeBusinesses(snapAll.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
        const found = all.filter((b) => matchBusinessToAnyKey(b, keysToMatch));
        if (found.length > 0) {
          if (request !== searchRequest.current) return;
          setBusinesses(found);
          setHasMore(false);
          lastVisibleRef.current = null;
          writeCache(cacheKey, { list: found, lastLoaded: Date.now() });
          return;
        }
      } catch (e) {
        console.warn("[SR] fallback scan failed:", e);
      }

      if (request !== searchRequest.current) return;

      setBusinesses([]);
      setHasMore(false);
      lastVisibleRef.current = null;
      writeCache(cacheKey, { list: [], lastLoaded: Date.now() });
    } catch (err) {
      console.error("[SR] loadFirstPage error", err);
      if (request !== searchRequest.current) return;
      setError(err && err.message ? err.message : String(err));
      if (request !== searchRequest.current) return;
      setBusinesses([]);
      setHasMore(false);
      lastVisibleRef.current = null;
    } finally {
      if (request !== searchRequest.current) return;
      setLoading(false);
    }
  }

  async function loadMore() {
    if (isIosCapacitor()) return;
    if (loadingMoreRef.current) return;
    if (!lastVisibleRef.current) return;
    loadingMoreRef.current = true;

    try {
      const col = query(collection(db, "businesses"), ...(readSearchMarket().code === "PA" ? [] : [where("countryCode", "==", readSearchMarket().code)]));
      const q0 = query(col, startAfter(lastVisibleRef.current), limit(PAGE_SIZE));
      const snap = await getDocs(q0);

      if (!snap || snap.empty) {
        setHasMore(false);
        return;
      }

      const newDocs = sanitizeBusinesses(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
      lastVisibleRef.current = snap.docs[snap.docs.length - 1] || null;

      setBusinesses((prev) => {
        const merged = sanitizeBusinesses([...(Array.isArray(prev) ? prev : []), ...newDocs]);
        writeCache(cacheKey, { list: merged, lastLoaded: Date.now() });
        return merged;
      });

      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.warn("[SR] loadMore failed", e);
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    lastVisibleRef.current = null;
    setBusinesses([]);
    setHasMore(false);

    try {
      const cached = readCache(cacheKey);
      if (cached && cached.list && Array.isArray(cached.list) && cached.list.length > 0) {
        setBusinesses(sanitizeBusinesses(cached.list));
        setTimeout(() => {
          if (!cancelled) loadFirstPage(paramRaw, PAGE_SIZE);
        }, 0);
        setLoading(false);
      } else {
        loadFirstPage(paramRaw, PAGE_SIZE);
      }
    } catch {
      loadFirstPage(paramRaw, PAGE_SIZE);
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramRaw, locationQueryParam, isSearchMode]);

  const subservices = useMemo(() => {
    const s = new Set();
    (businesses || []).forEach((b) => {
      const allServices = [
        ...(Array.isArray(b?.services) ? b.services : []),
        ...(Array.isArray(b?.__matchedServices) ? b.__matchedServices : []),
      ];
      for (const sv of allServices) {
        if (!sv) continue;
        if (typeof sv === "string") s.add(sv);
        else if (typeof sv === "object") {
          const nm = sv.name || sv.title || sv.label || sv.id || null;
          if (nm) s.add(String(nm));
        }
      }
    });
    return Array.from(s).slice(0, 40);
  }, [businesses]);

  const locationOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    (businesses || []).forEach((biz) => {
      const raw = businessLocationLabelForResult(biz);
      const clean = String(raw || "").trim();
      if (!clean || clean.toLowerCase() === "panamá") return;
      const key = normalizeName(clean);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(clean);
    });
    return out.slice(0, 12);
  }, [businesses]);

  const filteredLocationOptions = useMemo(() => {
    const base = [...new Set([selectedLocation, ...locationOptions, locationQueryParam, "San Francisco", "Costa del Este", "Obarrio", "Panamá"].filter(Boolean))];
    const q = normalizeName(locationFilterSearch);
    if (!q) return base.slice(0, 10);
    return base.filter((loc) => normalizeName(loc).includes(q)).slice(0, 10);
  }, [selectedLocation, locationOptions, locationQueryParam, locationFilterSearch]);

  const categoryOptions = useMemo(() => {
    return (serviceCats || []).slice(0, 8).map((cat) => ({
      id: cat.id,
      title: getFriendlyServiceTitle(cat.name || cat.title || cat.id, cat.name || cat.title || cat.id),
      raw: cat,
    }));
  }, [serviceCats]);

  const processed = useMemo(() => {
    let list = (businesses || []).slice();

    if (selectedSubservice) {
      const key = normalizeName(selectedSubservice);
      list = list.filter((b) => {
        try {
          const arr = [
            ...(Array.isArray(b.services) ? b.services : []),
            ...(Array.isArray(b.__matchedServices) ? b.__matchedServices : []),
          ];
          return arr.some((sv) => {
            if (!sv) return false;
            if (typeof sv === "string") {
              const n = normalizeName(sv);
              return n === key || n.includes(key) || key.includes(n);
            }
            const n = normalizeName([sv.name, sv.title, sv.label, sv.id, sv.category].filter(Boolean).join(" "));
            return n === key || n.includes(key) || key.includes(n);
          });
        } catch {
          return false;
        }
      });
    }

    if (selectedLocation) {
      const locKey = normalizeName(selectedLocation);
      list = list.filter((b) => normalizeName(businessTextForMarketplace(b)).includes(locKey) || normalizeName(businessLocationLabelForResult(b)).includes(locKey));
    }

    if (filters.online) list = list.filter((b) => (b.offers && b.offers.online) || b.online === true || b.virtual === true);
    if (filters.home) list = list.filter((b) => businessSupportsHomeService(b));
    if (filters.onsite) list = list.filter((b) => businessSupportsOnsiteService(b));
    if (filters.offer) list = list.filter((b) => (b.offers && b.offers.special) || b.specialOffer === true || b.hasOffer === true);
    if (filters.today) list = list.filter((b) => businessOpenOrAvailableToday(b));
    if (filters.rated) list = list.filter((b) => businessRatingValue(b) > 0);

    if (sortBy === "rating") list.sort((a, b) => businessRatingValue(b) - businessRatingValue(a));
    else if (sortBy === "location") list.sort((a, b) => businessLocationLabelForResult(a).localeCompare(businessLocationLabelForResult(b)));
    else if (sortBy === "new") list.sort((a, b) => businessUpdatedTime(b) - businessUpdatedTime(a));
    else list.sort((a, b) => {
      const rank = b => { const name = normalizeName(b.name || b.businessName || ""); return name === paramNorm ? 0 : name.startsWith(paramNorm) ? 1 : 2; };
      return (isSearchMode ? rank(a) - rank(b) : 0) || (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || businessRatingValue(b) - businessRatingValue(a);
    });

    return list;
  }, [businesses, selectedSubservice, selectedLocation, filters, sortBy, paramNorm, isSearchMode]);

  const activeFilterCount = [filters.online, filters.home, filters.onsite, filters.offer, filters.today, filters.rated, Boolean(selectedSubservice), Boolean(selectedLocation)].filter(Boolean).length;
  const anyFilterOn = activeFilterCount > 0;
  const sortLabels = { recommended: "Recomendado", rating: "Mejor valorados", location: "Ubicación", new: "Nuevos" };
  const sortShort = sortLabels[sortBy] || "Recomendado";
  const resetPremiumFilters = () => {
    setFilters({ online: false, home: false, onsite: false, offer: false, today: false, rated: false });
    setSelectedSubservice(null);
    setSelectedLocation(null);
    setSortBy("recommended");
  };

  const removeSingleFilter = (key) => {
    if (key === "scope") {
      navigate("/services/search?q=servicios");
      return;
    }
    if (key === "sort") {
      setSortBy("recommended");
      return;
    }
    if (key === "subservice") {
      setSelectedSubservice(null);
      return;
    }
    if (key === "location") {
      setSelectedLocation(null);
      return;
    }
    if (key && Object.prototype.hasOwnProperty.call(filters, key)) {
      setFilters((prev) => ({ ...prev, [key]: false }));
    }
  };

  const activeServiceId = paramRaw;

  const activeCategory = serviceCats.find((c) => String(c.id) === String(activeServiceId));
  const activeTitle = isSearchMode
    ? (paramRaw ? t('Resultados para “{query}”',{query:paramRaw}) : t("Explora Loop"))
    : activeCategory?.name || activeCategory?.title
      ? getFriendlyServiceTitle(activeCategory?.name || activeCategory?.title, "Servicios")
      : catsLoading
        ? "Cargando servicios..."
        : getFriendlyServiceTitle(paramRaw || paramNorm, "Servicios");
  const activeCopy = marketplaceHeroCopy({ title: activeTitle, isSearchMode, searchQuery: paramRaw });
  const activeFilterPills = [
    paramRaw || activeTitle ? { key: "scope", label: isSearchMode ? (paramRaw || "Búsqueda") : activeTitle } : null,
    selectedSubservice ? { key: "subservice", label: selectedSubservice } : null,
    selectedLocation ? { key: "location", label: selectedLocation } : null,
    filters.today ? { key: "today", label: "Hoy" } : null,
    filters.rated ? { key: "rated", label: "Con reseñas" } : null,
    filters.onsite ? { key: "onsite", label: "En local" } : null,
    filters.online ? { key: "online", label: "Online" } : null,
    filters.home ? { key: "home", label: "A domicilio" } : null,
    filters.offer ? { key: "offer", label: "Ofertas" } : null,
    sortBy !== "recommended" ? { key: "sort", label: `Orden: ${sortShort}` } : null,
  ].filter(Boolean);
  const resultCountText = loading && businesses.length === 0 ? "Cargando..." : `${processed.length} ${processed.length === 1 ? "negocio" : "negocios"}`;
  const featuredResults = []; // Search UX 1G: remove heavy featured block to reduce visual noise.
  const suggestionChips = ["Barbería", "Uñas", "Masajes"].filter((chip) => normalizeName(chip) !== normalizeName(paramRaw));
  const relatedEmptyChips = getSmartRelatedChips({ title: activeTitle, queryValue: paramRaw, isSearchMode })
    .filter((chip) => normalizeName(chip) !== normalizeName(paramRaw));
  const handleSmartChip = (chip) => {
    const key = normalizeName(chip);
    if (key.includes("evento")) navigate("/events");
    else if (key === "ver_todos") navigate("/services/search?q=servicios");
    else navigate(`/services/search?q=${encodeURIComponent(chip)}`);
  };


  useEffect(() => {
    // LOOP SERVICE RESULTS — PREMIUM RESERVE CTA V53
    // BusinessResultCard owns the card markup. This tags only the real "Reservar"
    // CTA and its card root so CSS can place it cleanly on mobile.
    if (typeof document === "undefined") return undefined;

    let rafId = 0;
    let timerId = 0;
    let observer = null;

    const findCardRoot = (btn) => {
      let el = btn;
      while (el && el !== document.body) {
        const parent = el.parentElement;
        if (
          parent &&
          (
            parent.classList?.contains("sr-main-list") ||
            parent.classList?.contains("sr-featured-card-grid") ||
            parent.classList?.contains("sr-featured-grid")
          )
        ) {
          return el;
        }
        el = parent;
      }
      return btn.closest("article, a, section, div");
    };

    const tagReserveButtons = () => {
      try {
        const root = document.querySelector(".sr-pro-page");
        if (!root) return;

        root.querySelectorAll(".sr-result-reserve-cta-v53").forEach((btn) => {
          btn.classList.remove("sr-result-reserve-cta-v53");
        });
        root.querySelectorAll(".sr-result-card-v53").forEach((card) => {
          card.classList.remove("sr-result-card-v53");
        });

        const buttons = Array.from(
          root.querySelectorAll(".sr-main-list button, .sr-featured-card-grid button, .sr-featured-grid button")
        );

        buttons.forEach((btn) => {
          const text = String(btn.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
          const label = String(btn.getAttribute("aria-label") || btn.getAttribute("title") || "").toLowerCase();
          const isReserve = text === "reservar" || label.includes("reservar");
          const looksLikeFavorite =
            label.includes("favorito") ||
            label.includes("favorite") ||
            text.includes("♥") ||
            text.includes("♡");

          if (!isReserve || looksLikeFavorite) return;

          btn.classList.add("sr-result-reserve-cta-v53");

          const card = findCardRoot(btn);
          if (card) {
            card.classList.add("sr-result-card-v53");
            card.setAttribute("data-loop-result-card", "v53");
          }
        });

        root.querySelectorAll(".sr-result-chip-v53, .sr-result-chip-hide-v53, .sr-result-chip-wrap-v53").forEach((el) => {
          el.classList.remove("sr-result-chip-v53", "sr-result-chip-hide-v53", "sr-result-chip-wrap-v53");
        });

        root.querySelectorAll(".sr-result-card-v53").forEach((card) => {
          const candidates = Array.from(card.querySelectorAll("span, em, small, button, div"))
            .filter((el) => !el.classList?.contains("sr-result-reserve-cta-v53"));

          candidates.forEach((el) => {
            const rawText = String(el.textContent || "").replace(/\s+/g, " ").trim();
            const text = rawText.toLowerCase();
            if (!rawText || rawText.length > 32) return;

            const isMetaChip =
              text === "a domicilio" ||
              text === "sirenita" ||
              text === "servicio principal" ||
              text === "principal" ||
              text === "online" ||
              text === "en local" ||
              text === "disponible hoy";

            if (!isMetaChip) return;

            if (text === "servicio principal" || text === "principal") {
              el.classList.add("sr-result-chip-hide-v53");
              return;
            }

            el.classList.add("sr-result-chip-v53");

            const parent = el.parentElement;
            if (parent && parent !== card) {
              parent.classList.add("sr-result-chip-wrap-v53");
            }
          });
        });
      } catch (_) {}
    };

    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(tagReserveButtons);
    };

    schedule();
    timerId = window.setTimeout(schedule, 240);

    try {
      const root = document.querySelector(".sr-pro-page");
      if (root && typeof MutationObserver !== "undefined") {
        observer = new MutationObserver(schedule);
        observer.observe(root, { childList: true, subtree: true });
      }
    } catch (_) {}

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (timerId) window.clearTimeout(timerId);
      if (observer) observer.disconnect();
    };
  }, [processed, loading]);

  const toggleFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const submitMarketplaceSearch = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const next = String(localSearch || "").trim();
    if (!next) return;
    navigate(`/services/search?q=${encodeURIComponent(next)}`);
  };

  const openFilterTab = (tab = "keyword") => {
    setFilterTab(tab);
    setFilterSheetOpen(true);
    setSortOpen(false);
    setServiceOpen(false);
    setLocationOpen(false);
  };

  const applyKeywordSearch = () => {
    const next = String(localSearch || "").trim();
    if (!next) return;
    setFilterSheetOpen(false);
    navigate(`/services/search?q=${encodeURIComponent(next)}`);
  };

  return (
    <div className="sr-pro-page">
      <style>{`
        .sr-pro-page {
          min-height: 100vh;
          background:
            radial-gradient(circle at 8% 0%, rgba(0,103,95,.085), transparent 28%),
            radial-gradient(circle at 92% 12%, rgba(20,184,166,.06), transparent 30%),
            linear-gradient(180deg, #ffffff 0%, #fbfdfd 42%, #ffffff 100%);
          color: #0f172a;
        }

        .sr-shell {
          width: min(100%, 1240px);
          margin: 0 auto;
          padding: 28px clamp(18px, 4.2vw, 54px) 54px;
          box-sizing: border-box;
        }

        .sr-hero {
          display: grid;
          gap: 18px;
          padding: 20px 0 18px;
          border-bottom: 1px solid rgba(203,213,225,.72);
        }

        .sr-topline {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
        }

        .sr-back-btn {
          border: 1px solid rgba(226,232,240,.95);
          background: rgba(255,255,255,.84);
          color: #334155;
          border-radius: 999px;
          min-height: 40px;
          padding: 0 14px;
          font-size: 13px;
          font-weight: 950;
          box-shadow: 0 10px 24px rgba(15,23,42,.045);
          cursor: pointer;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .sr-count-pill {
          border: 1px solid rgba(0,103,95,.13);
          background: rgba(236,253,245,.82);
          color: #00675f;
          border-radius: 999px;
          min-height: 38px;
          padding: 0 14px;
          display: inline-flex;
          align-items: center;
          font-size: 12px;
          font-weight: 950;
          white-space: nowrap;
        }

        .sr-title-block h1 {
          margin: 0;
          max-width: 760px;
          color: #07111f;
          font-size: clamp(40px, 4.8vw, 68px);
          line-height: .9;
          letter-spacing: -0.082em;
          font-weight: 950;
          text-wrap: balance;
        }

        .sr-title-block p {
          margin: 14px 0 0;
          max-width: 650px;
          color: #64748b;
          font-size: 16px;
          line-height: 1.5;
          font-weight: 680;
          letter-spacing: -0.01em;
        }

        .sr-category-rail-wrap {
          position: relative;
          z-index: 20;
          margin: 18px 0 0;
          padding: 0;
          background: transparent;
          border-bottom: 0;
          backdrop-filter: none;
          -webkit-backdrop-filter: none;
        }

        .services-top-scroll {
          display: flex;
          gap: 10px;
          align-items: center;
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding: 2px 0 4px;
          background: transparent;
        }

        .services-top-scroll::-webkit-scrollbar { display: none; }

        .sr-service-tile {
          flex: 0 0 auto;
          width: auto;
          min-width: auto;
          scroll-snap-align: start;
          border: 1px solid rgba(226,232,240,.92);
          background: rgba(255,255,255,.74);
          border-radius: 999px;
          padding: 7px 12px 7px 7px;
          cursor: pointer;
          text-align: left;
          display: inline-flex;
          align-items: center;
          gap: 9px;
          transition: transform 160ms ease, background 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
        }

        .sr-service-tile:hover {
          transform: translateY(-1px);
          border-color: rgba(0,103,95,.18);
          background: #ffffff;
          box-shadow: 0 12px 26px rgba(15,23,42,.055);
        }

        .sr-service-tile.is-active {
          background: #0f172a;
          border-color: #0f172a;
          box-shadow: 0 14px 32px rgba(15,23,42,.16);
        }

        .sr-service-img {
          width: 34px;
          height: 34px;
          border-radius: 999px;
          overflow: hidden;
          background: #ecfdf5;
          box-shadow: none;
          filter: grayscale(.15);
          opacity: .9;
          flex: 0 0 auto;
        }

        .sr-service-tile.is-active .sr-service-img {
          filter: none;
          opacity: 1;
          background: #ffffff;
        }

        .sr-service-img img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          object-position: center;
        }

        .sr-service-title {
          margin-top: 0;
          max-width: 150px;
          color: #334155;
          font-size: 12.5px;
          line-height: 1.1;
          font-weight: 900;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sr-service-tile.is-active .sr-service-title {
          color: #ffffff;
        }

        .sr-toolbar {
          position: relative;
          z-index: 18;
          margin: 16px 0 0;
          padding: 0;
          background: transparent;
          border-bottom: 0;
          backdrop-filter: none;
          -webkit-backdrop-filter: none;
        }

        .sr-toolbar-inner {
          display: flex;
          align-items: center;
          gap: 18px;
          flex-wrap: wrap;
        }

        .sr-toolbar-group {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .sr-toolbar-label {
          flex: 0 0 auto;
          color: #94a3b8;
          font-size: 10px;
          font-weight: 950;
          letter-spacing: .14em;
          text-transform: uppercase;
        }

        .sr-toolbar-divider {
          width: 1px;
          height: 28px;
          background: rgba(203,213,225,.9);
        }

        .sr-chip-row {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-bottom: 1px;
        }

        .sr-chip-row::-webkit-scrollbar { display: none; }

        .sr-chip,
        .sr-subservice-pill {
          flex: 0 0 auto;
          border: 1px solid rgba(226,232,240,.95);
          background: rgba(255,255,255,.92);
          color: #334155;
          min-height: 38px;
          border-radius: 999px;
          padding: 0 14px;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 13px;
          font-weight: 900;
          box-shadow: 0 8px 20px rgba(15,23,42,.04);
          cursor: pointer;
          white-space: nowrap;
        }

        .sr-chip.is-active,
        .sr-subservice-pill.is-active {
          background: #00675f;
          color: #ffffff;
          border-color: #00675f;
          box-shadow: 0 14px 28px rgba(0,103,95,.18);
        }

        .sr-chip.is-soft-active {
          background: #ecfdf5;
          color: #047857;
          border-color: #99f6e4;
        }

        .sr-subservice-strip {
          margin: 18px 0 18px;
          display: flex;
          gap: 9px;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-bottom: 3px;
        }

        .sr-subservice-strip::-webkit-scrollbar { display: none; }

        .sr-content-grid {
          display: block;
        }

        .sr-main-list {
          display: grid;
          gap: 14px;
        }

        .sr-side-panel {
          display: none;
        }

        .sr-empty,
        .sr-error,
        .sr-loading-card {
          border: 1px solid rgba(226,232,240,.92);
          background: rgba(255,255,255,.92);
          border-radius: 30px;
          padding: 24px;
          box-shadow: 0 18px 48px rgba(15,23,42,.055);
          color: #64748b;
          font-weight: 800;
        }

        .sr-load-more {
          margin-top: 22px;
          text-align: center;
        }

        .sr-load-more button {
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #0f172a;
          border-radius: 999px;
          min-height: 46px;
          padding: 0 20px;
          font-weight: 950;
          cursor: pointer;
          box-shadow: 0 10px 24px rgba(15,23,42,.055);
        }

        .sr-skeleton-line {
          height: 126px;
          border-radius: 26px;
          background: linear-gradient(90deg, #f1f5f9 0%, #f8fafc 50%, #f1f5f9 100%);
          background-size: 200% 100%;
          animation: srShimmer 1.25s infinite linear;
        }

        @keyframes srShimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }



        .sr-marketplace-kicker {
          display: inline-flex;
          align-items: center;
          width: fit-content;
          border: 1px solid rgba(0,103,95,.16);
          background: rgba(236,253,245,.82);
          color: #00675f;
          border-radius: 999px;
          padding: 7px 11px;
          font-size: 11px;
          font-weight: 950;
          letter-spacing: .10em;
          text-transform: uppercase;
          margin-bottom: 12px;
        }

        .sr-marketplace-search {
          max-width: 760px;
          min-height: 58px;
          display: grid;
          grid-template-columns: auto minmax(0,1fr) auto;
          gap: 10px;
          align-items: center;
          border: 1px solid rgba(226,232,240,.95);
          background: rgba(255,255,255,.94);
          border-radius: 999px;
          padding: 7px 8px 7px 18px;
          box-shadow: 0 18px 44px rgba(15,23,42,.075);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
        }

        .sr-marketplace-search > span {
          color: #00675f;
          font-size: 20px;
          font-weight: 950;
        }

        .sr-marketplace-search input {
          min-width: 0;
          height: 42px;
          border: 0;
          outline: 0;
          background: transparent;
          color: #0f172a;
          font-size: 15px;
          font-weight: 780;
        }

        .sr-marketplace-search input::placeholder {
          color: #94a3b8;
          font-weight: 720;
        }

        .sr-marketplace-search button {
          height: 44px;
          border: 0;
          border-radius: 999px;
          background: #0f172a;
          color: #fff;
          padding: 0 18px;
          font-weight: 950;
          cursor: pointer;
          box-shadow: 0 12px 26px rgba(15,23,42,.18);
        }

        .sr-marketplace-suggestions {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-bottom: 2px;
        }

        .sr-marketplace-suggestions::-webkit-scrollbar { display: none; }

        .sr-marketplace-suggestions button {
          flex: 0 0 auto;
          border: 1px solid rgba(226,232,240,.95);
          background: rgba(255,255,255,.82);
          color: #334155;
          border-radius: 999px;
          min-height: 36px;
          padding: 0 13px;
          font-size: 12.5px;
          font-weight: 900;
          cursor: pointer;
          box-shadow: 0 8px 20px rgba(15,23,42,.04);
        }

        .sr-featured-strip {
          margin: 18px 0 18px;
          border: 1px solid rgba(226,232,240,.92);
          background: linear-gradient(135deg, rgba(15,23,42,.96), rgba(0,103,95,.88));
          border-radius: 30px;
          padding: 18px;
          box-shadow: 0 20px 54px rgba(15,23,42,.12);
        }

        .sr-featured-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 12px;
          color: #fff;
        }

        .sr-featured-head span {
          color: rgba(255,255,255,.66);
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .12em;
          font-weight: 950;
        }

        .sr-featured-head strong {
          color: #fff;
          font-size: 15px;
          font-weight: 950;
        }

        .sr-featured-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0,1fr));
          gap: 10px;
        }

        .sr-featured-card-grid .brc-card {
          background: rgba(255,255,255,.96);
          box-shadow: 0 14px 38px rgba(0,0,0,.12);
        }

        .sr-premium-toolbar {
          position: sticky;
          top: 78px;
          z-index: 34;
          padding: 10px 0;
          margin-top: 14px;
          background: rgba(255,255,255,.82);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          border-bottom: 1px solid rgba(226,232,240,.72);
        }

        .sr-premium-toolbar-inner {
          gap: 12px;
        }

        .sr-filter-master-btn {
          flex: 0 0 auto;
          min-height: 42px;
          border-radius: 999px;
          border: 1px solid rgba(0,103,95,.18);
          background: #0f172a;
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 0 15px;
          font-size: 13px;
          font-weight: 950;
          cursor: pointer;
          box-shadow: 0 14px 30px rgba(15,23,42,.16);
        }

        .sr-filter-master-btn strong {
          min-width: 22px;
          height: 22px;
          border-radius: 999px;
          background: #14b8a6;
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
        }

        .sr-active-summary {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 10px;
        }

        .sr-active-summary span {
          border: 1px solid rgba(226,232,240,.92);
          background: rgba(248,250,252,.92);
          color: #64748b;
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 11px;
          font-weight: 850;
        }

        .sr-filter-sheet {
          position: fixed;
          inset: 0;
          z-index: 100020;
          display: flex;
          align-items: flex-end;
          justify-content: center;
        }

        .sr-filter-sheet-backdrop {
          position: absolute;
          inset: 0;
          border: 0;
          background: rgba(15,23,42,.38);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }

        .sr-filter-sheet-panel {
          position: relative;
          width: min(100%, 720px);
          max-height: min(82vh, 680px);
          overflow-y: auto;
          background: #ffffff;
          border: 1px solid rgba(226,232,240,.92);
          border-radius: 30px 30px 0 0;
          box-shadow: 0 -26px 70px rgba(15,23,42,.22);
          padding: 18px 18px calc(18px + env(safe-area-inset-bottom,0px));
        }

        .sr-filter-sheet-head, .sr-filter-sheet-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .sr-filter-sheet-head span {
          display: block;
          color: #00675f;
          font-size: 11px;
          font-weight: 950;
          letter-spacing: .12em;
          text-transform: uppercase;
        }

        .sr-filter-sheet-head strong {
          display: block;
          margin-top: 4px;
          color: #0f172a;
          font-size: 22px;
          font-weight: 950;
          letter-spacing: -.04em;
        }

        .sr-filter-sheet-head button {
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #334155;
          border-radius: 999px;
          min-height: 38px;
          padding: 0 13px;
          font-weight: 900;
        }

        .sr-filter-sheet-section {
          margin-top: 18px;
          border-top: 1px solid #f1f5f9;
          padding-top: 16px;
        }

        .sr-filter-sheet-section h4 {
          margin: 0 0 10px;
          color: #0f172a;
          font-size: 14px;
          font-weight: 950;
        }

        .sr-sheet-options {
          display: grid;
          grid-template-columns: repeat(2, minmax(0,1fr));
          gap: 9px;
        }

        .sr-sheet-options button {
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #334155;
          border-radius: 18px;
          min-height: 48px;
          padding: 0 12px;
          text-align: left;
          font-weight: 900;
          cursor: pointer;
        }

        .sr-sheet-options button.active {
          border-color: #99f6e4;
          background: #ecfdf5;
          color: #00675f;
          box-shadow: 0 0 0 4px rgba(20,184,166,.10);
        }

        .sr-filter-sheet-actions {
          margin-top: 20px;
          position: sticky;
          bottom: 0;
          background: linear-gradient(180deg, rgba(255,255,255,0), #fff 18%);
          padding-top: 18px;
        }

        .sr-filter-sheet-actions button {
          flex: 1;
          min-height: 50px;
          border-radius: 17px;
          font-weight: 950;
          cursor: pointer;
        }

        .sr-filter-sheet-actions .secondary { border: 1px solid #e2e8f0; background: #fff; color: #334155; }
        .sr-filter-sheet-actions .primary { border: 0; background: #00675f; color: #fff; box-shadow: 0 14px 28px rgba(0,103,95,.20); }


        .sr-smart-empty {
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(226,232,240,.92);
          background:
            radial-gradient(circle at 12% 0%, rgba(20,184,166,.12), transparent 28%),
            linear-gradient(135deg, rgba(255,255,255,.98), rgba(248,250,252,.94));
          border-radius: 32px;
          padding: 26px;
          box-shadow: 0 24px 70px rgba(15,23,42,.08);
          display: grid;
          gap: 16px;
        }
        .sr-smart-empty-glow {
          width: 56px;
          height: 56px;
          border-radius: 22px;
          display: grid;
          place-items: center;
          background: #0f172a;
          color: #fff;
          box-shadow: 0 18px 40px rgba(15,23,42,.18);
        }
        .sr-smart-empty-glow span { font-size: 28px; font-weight: 950; transform: translateY(-1px); }
        .sr-smart-empty-kicker {
          color: #00675f;
          font-size: 11px;
          font-weight: 950;
          letter-spacing: .14em;
          text-transform: uppercase;
          margin-bottom: 8px;
        }
        .sr-smart-empty h3 {
          margin: 0;
          color: #0f172a;
          font-size: clamp(24px, 3vw, 34px);
          line-height: 1.02;
          letter-spacing: -.055em;
          font-weight: 950;
        }
        .sr-smart-empty p {
          margin: 10px 0 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.5;
          font-weight: 720;
          max-width: 680px;
        }
        .sr-smart-empty-chips {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .sr-smart-empty-chips button {
          border: 1px solid rgba(0,103,95,.14);
          background: #fff;
          color: #0f766e;
          border-radius: 999px;
          min-height: 38px;
          padding: 0 14px;
          font-size: 13px;
          font-weight: 900;
          cursor: pointer;
          box-shadow: 0 8px 20px rgba(15,23,42,.04);
        }
        .sr-smart-empty-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .sr-smart-empty-actions button {
          border-radius: 999px;
          min-height: 44px;
          padding: 0 18px;
          font-size: 13px;
          font-weight: 950;
          cursor: pointer;
        }
        .sr-smart-empty-actions .primary { border: 0; background: #00675f; color: #fff; box-shadow: 0 14px 28px rgba(0,103,95,.20); }
        .sr-smart-empty-actions .secondary { border: 1px solid #e2e8f0; background: #fff; color: #334155; }

        @media (max-width: 900px) {
          .sr-shell {
            padding: 16px 14px calc(96px + env(safe-area-inset-bottom, 0px));
          }

          .sr-hero {
            padding-top: 12px;
            gap: 14px;
          }

          .sr-title-block h1 {
            font-size: clamp(36px, 10.8vw, 48px);
            line-height: .92;
            letter-spacing: -0.074em;
          }

          .sr-title-block p {
            font-size: 14px;
            margin-top: 10px;
            line-height: 1.45;
          }

          .sr-category-rail-wrap {
            top: auto;
            margin-left: 0;
            margin-right: 0;
            padding-left: 0;
            padding-right: 0;
          }

          .sr-toolbar {
            top: auto;
            margin-left: 0;
            margin-right: 0;
            padding-left: 0;
            padding-right: 0;
          }


          .sr-premium-toolbar {
            top: auto;
            margin-left: -14px;
            margin-right: -14px;
            padding: 10px 14px;
          }

          .sr-toolbar-inner {
            align-items: flex-start;
            gap: 12px;
          }

          .sr-filter-master-btn {
            width: 100%;
            justify-content: center;
          }

          .sr-toolbar-group {
            width: 100%;
            display: grid;
            gap: 7px;
          }

          .sr-toolbar-divider {
            display: none;
          }

          .sr-toolbar-label {
            padding-left: 2px;
          }

          .sr-service-img {
            width: 30px;
            height: 30px;
          }

          .sr-service-title {
            max-width: 126px;
            font-size: 12px;
          }

          .sr-subservice-strip {
            margin-top: 14px;
            margin-bottom: 14px;
          }

          .sr-main-list {
            gap: 12px;
          }

          .sr-marketplace-search {
            border-radius: 24px;
            grid-template-columns: auto minmax(0,1fr);
            padding: 8px 10px 10px 14px;
          }

          .sr-marketplace-search button {
            grid-column: 1 / -1;
            width: 100%;
          }

          .sr-featured-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 520px) {
          .sr-topline {
            align-items: flex-start;
          }

          .sr-count-pill {
            min-height: 34px;
            padding: 0 11px;
            font-size: 11px;
          }

          .sr-back-btn {
            min-height: 36px;
            padding: 0 12px;
          }

          .sr-chip,
          .sr-subservice-pill {
            min-height: 36px;
            padding: 0 12px;
            font-size: 12.5px;
          }
        }


        /* =========================================================
           SEARCH UX 1G — Clean results UI
           Reduce visual noise, remove heavy marketplace blocks, and make
           the results page feel calmer and closer to the Main Menu cards.
           ========================================================= */
        .sr-pro-page {
          background: linear-gradient(180deg, #ffffff 0%, #fbfdfd 100%) !important;
        }
        .sr-shell {
          max-width: 1120px !important;
          padding-top: 22px !important;
        }
        .sr-hero {
          gap: 12px !important;
          padding: 14px 0 16px !important;
          border-bottom: 1px solid rgba(226,232,240,.82) !important;
        }
        .sr-marketplace-kicker {
          font-size: 9.5px !important;
          padding: 5px 9px !important;
          margin-bottom: 9px !important;
          background: rgba(236,253,245,.62) !important;
          border-color: rgba(0,103,95,.12) !important;
        }
        .sr-title-block h1 {
          font-size: clamp(34px, 4vw, 54px) !important;
          letter-spacing: -0.072em !important;
          line-height: .94 !important;
        }
        .sr-title-block p {
          margin-top: 9px !important;
          max-width: 560px !important;
          font-size: 13.5px !important;
          line-height: 1.42 !important;
          color: #64748b !important;
        }
        .sr-count-pill, .sr-back-btn {
          box-shadow: none !important;
          min-height: 34px !important;
          font-size: 11.5px !important;
        }
        .sr-marketplace-search {
          max-width: 560px !important;
          min-height: 46px !important;
          padding: 5px 6px 5px 14px !important;
          box-shadow: 0 10px 24px rgba(15,23,42,.055) !important;
          border-color: rgba(226,232,240,.84) !important;
        }
        .sr-marketplace-search input {
          height: 34px !important;
          font-size: 13px !important;
        }
        .sr-marketplace-search button {
          height: 34px !important;
          padding: 0 14px !important;
          font-size: 12px !important;
          box-shadow: none !important;
        }
        .sr-marketplace-suggestions {
          max-width: 560px !important;
          gap: 6px !important;
        }
        .sr-marketplace-suggestions button {
          min-height: 30px !important;
          padding: 0 10px !important;
          font-size: 11.5px !important;
          box-shadow: none !important;
          background: #fff !important;
        }
        .sr-category-rail-wrap {
          margin-top: 12px !important;
        }
        .services-top-scroll {
          gap: 7px !important;
        }
        .sr-service-tile {
          padding: 5px 10px 5px 5px !important;
          border-color: rgba(226,232,240,.72) !important;
          background: rgba(255,255,255,.82) !important;
          box-shadow: none !important;
        }
        .sr-service-tile:hover {
          box-shadow: none !important;
          transform: none !important;
        }
        .sr-service-img {
          width: 26px !important;
          height: 26px !important;
        }
        .sr-service-title {
          font-size: 11.5px !important;
          max-width: 120px !important;
        }
        .sr-toolbar {
          margin-top: 12px !important;
          position: sticky !important;
          top: calc(66px + env(safe-area-inset-top, 0px)) !important;
          z-index: 15 !important;
          padding: 8px 0 !important;
          background: rgba(255,255,255,.88) !important;
          backdrop-filter: blur(12px) !important;
          -webkit-backdrop-filter: blur(12px) !important;
          border-bottom: 1px solid rgba(226,232,240,.70) !important;
        }
        .sr-toolbar-inner {
          gap: 10px !important;
        }
        .sr-toolbar-label, .sr-toolbar-divider, .sr-active-summary {
          display: none !important;
        }
        .sr-filter-master-btn, .sr-chip {
          min-height: 32px !important;
          padding: 0 11px !important;
          font-size: 11.5px !important;
          box-shadow: none !important;
        }
        .sr-filter-group .sr-chip-row button:nth-child(n+4) {
          display: none !important;
        }
        .sr-featured-strip,
        .sr-subservice-strip,
        .sr-side-panel {
          display: none !important;
        }
        .sr-content-grid {
          margin-top: 14px !important;
        }
        .sr-main-list {
          gap: 12px !important;
          max-width: 860px !important;
        }
        .sr-error, .sr-empty, .sr-loading-card, .sr-smart-empty {
          box-shadow: 0 12px 32px rgba(15,23,42,.045) !important;
          border-radius: 24px !important;
        }
        @media (max-width: 900px) {
          .sr-shell { padding-top: 12px !important; }
          .sr-title-block h1 { font-size: clamp(32px, 9.4vw, 44px) !important; }
          .sr-marketplace-search { max-width: 100% !important; }
          .sr-toolbar { top: calc(112px + env(safe-area-inset-top, 0px)) !important; margin-left: -14px !important; margin-right: -14px !important; padding-left: 14px !important; padding-right: 14px !important; }
          .sr-toolbar-inner { display: flex !important; overflow-x: auto !important; flex-wrap: nowrap !important; scrollbar-width: none !important; }
          .sr-toolbar-inner::-webkit-scrollbar { display: none !important; }
          .sr-toolbar-group { width: auto !important; display: flex !important; }
          .sr-filter-group .sr-chip-row button:nth-child(n+4) { display: inline-flex !important; }
        }


        /* =========================================================
           LOOP SEARCH RESULTS — MINIMAL WARM MARKETPLACE REVAMP
           Objetivo: menos Encuentra24, más Loop.
           Limpia hero, reduce chips, suaviza filtros y cards.
           ========================================================= */

        .sr-pro-page{
          background:
            radial-gradient(circle at 16% -8%, rgba(0,103,95,.10), transparent 26%),
            radial-gradient(circle at 88% 4%, rgba(20,184,166,.07), transparent 28%),
            linear-gradient(180deg,#fffaf2 0%,#ffffff 34%,#fbfdfc 100%) !important;
        }

        .sr-shell{
          width:min(100%,1120px) !important;
          padding:24px clamp(18px,4.4vw,52px) 62px !important;
        }

        .sr-hero{
          position:relative !important;
          gap:14px !important;
          padding:18px 0 20px !important;
          border-bottom:1px solid rgba(226,232,240,.88) !important;
        }

        .sr-topline{
          margin-bottom:2px !important;
        }

        .sr-back-btn{
          min-height:36px !important;
          padding:0 13px !important;
          border-radius:999px !important;
          background:rgba(255,255,255,.86) !important;
          color:#334155 !important;
          box-shadow:0 8px 20px rgba(15,23,42,.045) !important;
        }

        .sr-count-pill{
          min-height:34px !important;
          padding:0 13px !important;
          background:#ecfdf5 !important;
          border-color:rgba(0,103,95,.14) !important;
          color:#00675f !important;
        }

        .sr-title-block{
          max-width:720px !important;
        }

        .sr-marketplace-kicker{
          min-height:28px !important;
          padding:0 11px !important;
          margin-bottom:10px !important;
          background:#f0fdfa !important;
          border-color:rgba(0,103,95,.13) !important;
          font-size:10.5px !important;
          letter-spacing:.13em !important;
        }

        .sr-title-block h1{
          max-width:760px !important;
          margin:0 !important;
          color:#07142b !important;
          font-size:clamp(42px,5.7vw,70px) !important;
          line-height:.92 !important;
          letter-spacing:-.078em !important;
          font-weight:950 !important;
          text-wrap:balance !important;
        }

        .sr-title-block p{
          max-width:590px !important;
          margin:12px 0 0 !important;
          color:#64748b !important;
          font-size:15.5px !important;
          line-height:1.45 !important;
          font-weight:760 !important;
        }

        .sr-marketplace-search{
          margin-top:4px !important;
          max-width:640px !important;
          min-height:54px !important;
          grid-template-columns:24px minmax(0,1fr) auto !important;
          gap:9px !important;
          padding:6px 7px 6px 16px !important;
          border-radius:999px !important;
          background:rgba(255,255,255,.96) !important;
          border-color:rgba(226,232,240,.96) !important;
          box-shadow:0 18px 42px rgba(15,23,42,.065) !important;
        }

        .sr-marketplace-search > span{
          color:#00675f !important;
          font-size:18px !important;
        }

        .sr-marketplace-search input{
          height:40px !important;
          font-size:14px !important;
          font-weight:820 !important;
        }

        .sr-marketplace-search button{
          height:40px !important;
          padding:0 16px !important;
          background:#0f172a !important;
          box-shadow:none !important;
          font-size:12.5px !important;
        }

        .sr-marketplace-suggestions{
          padding-top:2px !important;
          max-width:640px !important;
        }

        .sr-marketplace-suggestions button{
          min-height:32px !important;
          padding:0 12px !important;
          background:#ffffff !important;
          border-color:rgba(226,232,240,.94) !important;
          box-shadow:none !important;
          color:#475569 !important;
          font-size:12px !important;
        }

        .sr-category-rail-wrap{
          margin:16px 0 0 !important;
          padding:0 0 14px !important;
          border-bottom:1px solid rgba(226,232,240,.82) !important;
        }

        .services-top-scroll{
          gap:8px !important;
          padding:0 0 2px !important;
        }

        .sr-service-tile{
          min-height:38px !important;
          padding:5px 11px 5px 5px !important;
          border-radius:999px !important;
          background:rgba(255,255,255,.88) !important;
          box-shadow:none !important;
        }

        .sr-service-img{
          width:28px !important;
          height:28px !important;
        }

        .sr-service-title{
          max-width:132px !important;
          font-size:12px !important;
          font-weight:920 !important;
        }

        .sr-service-tile.is-active{
          background:#0f172a !important;
          border-color:#0f172a !important;
          box-shadow:0 10px 24px rgba(15,23,42,.13) !important;
        }

        .sr-premium-toolbar{
          position:relative !important;
          top:auto !important;
          z-index:12 !important;
          margin:14px 0 0 !important;
          padding:12px !important;
          border:1px solid rgba(226,232,240,.92) !important;
          border-radius:24px !important;
          background:rgba(255,255,255,.84) !important;
          box-shadow:0 16px 40px rgba(15,23,42,.055) !important;
          backdrop-filter:blur(18px) !important;
          -webkit-backdrop-filter:blur(18px) !important;
        }

        .sr-premium-toolbar-inner{
          display:grid !important;
          grid-template-columns:auto minmax(0,1fr) minmax(0,1.1fr) !important;
          align-items:center !important;
          gap:12px !important;
        }

        .sr-filter-master-btn{
          min-height:38px !important;
          padding:0 14px !important;
          background:#0f172a !important;
          border-radius:999px !important;
          box-shadow:none !important;
        }

        .sr-toolbar-divider{
          display:none !important;
        }

        .sr-toolbar-group{
          width:auto !important;
          min-width:0 !important;
          display:grid !important;
          gap:6px !important;
        }

        .sr-toolbar-label{
          display:none !important;
        }

        .sr-chip-row{
          gap:7px !important;
          padding:0 !important;
        }

        .sr-chip{
          min-height:36px !important;
          padding:0 12px !important;
          box-shadow:none !important;
          font-size:12.2px !important;
          background:#fff !important;
          border-color:rgba(226,232,240,.94) !important;
          color:#475569 !important;
        }

        .sr-chip.is-active{
          background:#00675f !important;
          border-color:#00675f !important;
          color:#fff !important;
          box-shadow:0 10px 22px rgba(0,103,95,.16) !important;
        }

        .sr-chip.is-soft-active{
          background:#ecfdf5 !important;
          border-color:#99f6e4 !important;
          color:#00675f !important;
          box-shadow:none !important;
        }

        .sr-chip-clear{
          color:#b42318 !important;
          border-color:#fed7aa !important;
          background:#fff7ed !important;
        }

        .sr-active-summary{
          margin-top:10px !important;
          gap:7px !important;
        }

        .sr-active-summary span{
          background:#f8fafc !important;
          border-color:#edf2f7 !important;
          color:#64748b !important;
          font-size:10.8px !important;
        }

        .sr-results-headline{
          margin:18px 0 12px !important;
          display:flex !important;
          align-items:flex-end !important;
          justify-content:space-between !important;
          gap:12px !important;
        }

        .sr-results-headline span{
          display:block !important;
          color:#00675f !important;
          font-size:10.5px !important;
          font-weight:950 !important;
          letter-spacing:.14em !important;
          text-transform:uppercase !important;
          margin-bottom:4px !important;
        }

        .sr-results-headline strong{
          display:block !important;
          color:#07142b !important;
          font-size:22px !important;
          line-height:1 !important;
          letter-spacing:-.045em !important;
          font-weight:950 !important;
        }

        .sr-results-headline button{
          min-height:34px !important;
          border-radius:999px !important;
          border:1px solid rgba(226,232,240,.95) !important;
          background:#fff !important;
          color:#475569 !important;
          padding:0 12px !important;
          font-size:12px !important;
          font-weight:900 !important;
          cursor:pointer !important;
        }

        .sr-main-list{
          gap:13px !important;
        }

        .sr-loading-card{
          border-radius:26px !important;
          box-shadow:none !important;
        }

        /* BusinessResultCard visual softening, if the component exposes brc classes */
        .sr-main-list .brc-card{
          border-radius:28px !important;
          border-color:rgba(226,232,240,.95) !important;
          box-shadow:0 16px 44px rgba(15,23,42,.06) !important;
          background:#fff !important;
        }

        .sr-main-list .brc-card:hover{
          transform:translateY(-1px) !important;
          box-shadow:0 22px 52px rgba(15,23,42,.08) !important;
        }

        .sr-main-list .brc-media,
        .sr-main-list .brc-logo,
        .sr-main-list .brc-image,
        .sr-main-list .brc-thumb{
          border-radius:20px !important;
        }

        .sr-main-list .brc-kicker,
        .sr-main-list .brc-context{
          color:#00675f !important;
          letter-spacing:.13em !important;
        }

        .sr-main-list .brc-book,
        .sr-main-list .brc-primary,
        .sr-main-list .brc-actions button:last-child{
          background:#00796f !important;
          border-color:#00796f !important;
          color:#fff !important;
          box-shadow:0 12px 26px rgba(0,103,95,.18) !important;
        }

        .sr-filter-sheet{
          z-index:2147483646 !important;
        }

        .sr-filter-sheet-backdrop{
          background:rgba(15,23,42,.50) !important;
          backdrop-filter:blur(12px) !important;
          -webkit-backdrop-filter:blur(12px) !important;
        }

        .sr-filter-sheet-panel{
          border-radius:28px 28px 0 0 !important;
          box-shadow:0 -30px 84px rgba(15,23,42,.26) !important;
        }

        @media (min-width: 1100px){
          .sr-title-block h1{
            max-width:720px !important;
          }

          .sr-marketplace-title-block{
            max-width:760px !important;
          }
        }

        @media (max-width: 980px){
          .sr-shell{
            padding:18px 14px calc(88px + env(safe-area-inset-bottom,0px)) !important;
          }

          .sr-hero{
            padding-top:14px !important;
          }

          .sr-title-block h1{
            font-size:clamp(34px,10.5vw,48px) !important;
            letter-spacing:-.07em !important;
          }

          .sr-title-block p{
            font-size:14px !important;
          }

          .sr-marketplace-search{
            max-width:none !important;
            border-radius:22px !important;
            grid-template-columns:22px minmax(0,1fr) auto !important;
          }

          .sr-marketplace-search button{
            padding:0 13px !important;
          }

          .sr-premium-toolbar{
            margin-left:0 !important;
            margin-right:0 !important;
            padding:10px !important;
            border-radius:22px !important;
          }

          .sr-premium-toolbar-inner{
            grid-template-columns:1fr !important;
            gap:9px !important;
          }

          .sr-filter-master-btn{
            width:100% !important;
            justify-content:center !important;
          }

          .sr-active-summary{
            display:none !important;
          }

          .sr-category-rail-wrap{
            margin-top:14px !important;
            padding-bottom:12px !important;
          }

          .sr-results-headline{
            margin-top:16px !important;
          }
        }

        @media (max-width: 540px){
          .sr-topline{
            align-items:flex-start !important;
          }

          .sr-count-pill{
            font-size:11.5px !important;
          }

          .sr-marketplace-search{
            grid-template-columns:22px minmax(0,1fr) !important;
            padding:10px 12px !important;
          }

          .sr-marketplace-search button{
            grid-column:1 / -1 !important;
            width:100% !important;
            height:42px !important;
            margin-top:2px !important;
          }

          .sr-results-headline{
            align-items:flex-start !important;
          }

          .sr-results-headline strong{
            font-size:20px !important;
          }
        }



        /* =========================================================
           LOOP SEARCH RESULTS — V2 EDITORIAL LAYOUT + CARD POLISH
           Segunda vuelta: más ancho útil, filtros más calmados y cards más premium.
           ========================================================= */

        .sr-pro-page{
          min-height:100vh !important;
          background:
            radial-gradient(circle at 20% -8%, rgba(0,103,95,.12), transparent 24%),
            radial-gradient(circle at 92% 7%, rgba(255,248,238,.92), transparent 32%),
            linear-gradient(180deg,#fffaf2 0%,#ffffff 28%,#fbfdfc 100%) !important;
        }

        .sr-shell{
          width:min(100%,1260px) !important;
          max-width:1260px !important;
          margin:0 auto !important;
          padding:34px clamp(22px,4.1vw,58px) 72px !important;
        }

        .sr-hero{
          padding:18px 0 18px !important;
          gap:12px !important;
        }

        .sr-title-block{
          max-width:820px !important;
        }

        .sr-title-block h1{
          max-width:820px !important;
          font-size:clamp(44px,5vw,64px) !important;
          line-height:.94 !important;
          letter-spacing:-.074em !important;
        }

        .sr-title-block p{
          max-width:620px !important;
          margin-top:11px !important;
          color:#5f6f84 !important;
        }

        .sr-marketplace-search{
          width:min(100%,620px) !important;
          margin-top:8px !important;
        }

        .sr-marketplace-suggestions{
          width:min(100%,620px) !important;
        }

        .sr-category-rail-wrap{
          margin-top:14px !important;
          padding-bottom:12px !important;
        }

        .services-top-scroll{
          display:flex !important;
          align-items:center !important;
          gap:8px !important;
        }

        .sr-service-tile{
          height:38px !important;
          min-height:38px !important;
          background:#fff !important;
          border:1px solid rgba(226,232,240,.95) !important;
        }

        .sr-service-title{
          max-width:150px !important;
        }

        .sr-premium-toolbar{
          max-width:960px !important;
          margin:16px 0 0 !important;
          padding:9px !important;
          border-radius:999px !important;
          background:rgba(255,255,255,.86) !important;
          box-shadow:0 16px 44px rgba(15,23,42,.055) !important;
          border-color:rgba(226,232,240,.94) !important;
        }

        .sr-premium-toolbar-inner{
          display:flex !important;
          align-items:center !important;
          gap:8px !important;
          min-width:0 !important;
        }

        .sr-filter-master-btn{
          min-height:40px !important;
          height:40px !important;
          padding:0 14px !important;
          border-radius:999px !important;
          flex:0 0 auto !important;
        }

        .sr-sort-group,
        .sr-filter-group{
          display:flex !important;
          min-width:0 !important;
          width:auto !important;
        }

        .sr-sort-group{
          flex:0 0 auto !important;
        }

        .sr-filter-group{
          flex:1 1 auto !important;
          overflow:hidden !important;
        }

        .sr-chip-row{
          display:flex !important;
          flex-wrap:nowrap !important;
          gap:6px !important;
          overflow-x:auto !important;
          scrollbar-width:none !important;
          max-width:100% !important;
        }

        .sr-chip-row::-webkit-scrollbar{
          display:none !important;
        }

        .sr-chip{
          flex:0 0 auto !important;
          min-height:40px !important;
          height:40px !important;
          padding:0 13px !important;
          border-radius:999px !important;
          font-size:12px !important;
          font-weight:920 !important;
        }

        .sr-chip.is-active{
          background:#00796f !important;
          border-color:#00796f !important;
        }

        .sr-chip.is-soft-active{
          background:#f0fdfa !important;
          color:#00675f !important;
          border-color:rgba(0,103,95,.18) !important;
        }

        .sr-active-summary{
          display:none !important;
        }

        .sr-results-headline{
          max-width:960px !important;
          margin:22px 0 12px !important;
          padding:0 2px !important;
        }

        .sr-results-headline span{
          color:#00796f !important;
        }

        .sr-results-headline strong{
          font-size:24px !important;
        }

        .sr-content-grid{
          width:min(100%,960px) !important;
          max-width:960px !important;
        }

        .sr-main-list{
          gap:14px !important;
        }

        /* BusinessResultCard: target common result-card class names without changing its logic */
        .sr-main-list .brc-card,
        .sr-main-list [class*="brc-card"],
        .sr-main-list [class*="result-card"],
        .sr-main-list article{
          border-radius:28px !important;
          border:1px solid rgba(226,232,240,.96) !important;
          background:#ffffff !important;
          box-shadow:0 14px 38px rgba(15,23,42,.052) !important;
          transition:transform .16s ease, box-shadow .16s ease, border-color .16s ease !important;
        }

        .sr-main-list .brc-card:hover,
        .sr-main-list [class*="brc-card"]:hover,
        .sr-main-list [class*="result-card"]:hover,
        .sr-main-list article:hover{
          transform:translateY(-1px) !important;
          border-color:rgba(0,103,95,.18) !important;
          box-shadow:0 22px 54px rgba(15,23,42,.075) !important;
        }

        .sr-main-list .brc-media,
        .sr-main-list .brc-image,
        .sr-main-list .brc-logo,
        .sr-main-list .brc-thumb,
        .sr-main-list [class*="media"],
        .sr-main-list [class*="logo"],
        .sr-main-list [class*="thumb"]{
          border-radius:20px !important;
        }

        .sr-main-list .brc-title,
        .sr-main-list h3{
          color:#07142b !important;
          font-weight:950 !important;
          letter-spacing:-.045em !important;
        }

        .sr-main-list .brc-context,
        .sr-main-list .brc-kicker,
        .sr-main-list [class*="context"],
        .sr-main-list [class*="kicker"]{
          color:#00796f !important;
          font-size:10.5px !important;
          font-weight:950 !important;
          letter-spacing:.14em !important;
          text-transform:uppercase !important;
        }

        .sr-main-list .brc-description,
        .sr-main-list p{
          color:#5f6f84 !important;
          font-weight:760 !important;
        }

        .sr-main-list .brc-actions,
        .sr-main-list [class*="actions"]{
          align-items:center !important;
        }

        .sr-main-list .brc-actions button,
        .sr-main-list [class*="actions"] button{
          min-height:40px !important;
          border-radius:999px !important;
          font-size:12px !important;
          font-weight:950 !important;
        }

        .sr-main-list .brc-actions button:last-child,
        .sr-main-list [class*="actions"] button:last-child{
          background:#00796f !important;
          border-color:#00796f !important;
          color:#fff !important;
          box-shadow:0 12px 24px rgba(0,103,95,.16) !important;
        }

        .sr-load-more button{
          background:#0f172a !important;
          color:#fff !important;
          border-color:#0f172a !important;
          min-height:48px !important;
          box-shadow:0 14px 30px rgba(15,23,42,.12) !important;
        }

        @media (min-width: 1280px){
          .sr-shell{
            transform:translateX(-18px) !important;
          }
        }

        @media (max-width: 980px){
          .sr-shell{
            width:100% !important;
            padding:22px 16px calc(92px + env(safe-area-inset-bottom,0px)) !important;
            transform:none !important;
          }

          .sr-title-block h1{
            font-size:clamp(34px,10vw,48px) !important;
          }

          .sr-premium-toolbar{
            max-width:none !important;
            border-radius:22px !important;
          }

          .sr-premium-toolbar-inner{
            display:grid !important;
            grid-template-columns:1fr !important;
            gap:8px !important;
          }

          .sr-filter-master-btn{
            width:100% !important;
            justify-content:center !important;
          }

          .sr-sort-group,
          .sr-filter-group{
            width:100% !important;
            overflow:hidden !important;
          }

          .sr-content-grid,
          .sr-results-headline{
            width:100% !important;
            max-width:none !important;
          }
        }

        @media (max-width: 540px){
          .sr-shell{
            padding-left:13px !important;
            padding-right:13px !important;
          }

          .sr-hero{
            padding-top:12px !important;
          }

          .sr-marketplace-kicker{
            font-size:9.8px !important;
          }

          .sr-title-block h1{
            line-height:.98 !important;
          }

          .sr-service-tile{
            max-width:70vw !important;
          }

          .sr-results-headline{
            margin-top:18px !important;
          }

          .sr-main-list{
            gap:12px !important;
          }
        }



        /* =========================================================
           LOOP SEARCH RESULTS — V3 REBOUND STRONG
           Compact control surface. No hero. No category rail. No chip wall.
           ========================================================= */
        .sr-pro-page{
          background:linear-gradient(180deg,#fffaf2 0%,#ffffff 22%,#fbfdfc 100%) !important;
        }

        .sr-shell{
          width:min(100%,1050px) !important;
          max-width:1050px !important;
          margin:0 auto !important;
          padding:18px clamp(16px,3.2vw,38px) 70px !important;
          transform:none !important;
        }

        .sr-compact-top{
          display:grid !important;
          gap:10px !important;
          padding:8px 0 12px !important;
          border-bottom:1px solid rgba(226,232,240,.85) !important;
        }

        .sr-compact-row{
          display:flex !important;
          align-items:center !important;
          justify-content:space-between !important;
          gap:12px !important;
        }

        .sr-compact-back{
          min-height:34px !important;
          padding:0 12px !important;
          font-size:12px !important;
          box-shadow:none !important;
          background:#fff !important;
        }

        .sr-compact-title{
          min-width:0 !important;
          flex:1 !important;
          display:flex !important;
          align-items:center !important;
          justify-content:flex-end !important;
          gap:10px !important;
          text-align:right !important;
        }

        .sr-compact-title span{
          flex:0 0 auto !important;
          min-height:30px !important;
          display:inline-flex !important;
          align-items:center !important;
          padding:0 11px !important;
          border-radius:999px !important;
          background:#ecfdf5 !important;
          border:1px solid rgba(0,103,95,.14) !important;
          color:#00675f !important;
          font-size:11.5px !important;
          font-weight:950 !important;
          white-space:nowrap !important;
        }

        .sr-compact-title h1{
          margin:0 !important;
          max-width:560px !important;
          color:#07142b !important;
          font-size:clamp(24px,3vw,38px) !important;
          line-height:1 !important;
          letter-spacing:-.065em !important;
          font-weight:950 !important;
          white-space:nowrap !important;
          overflow:hidden !important;
          text-overflow:ellipsis !important;
        }

        .sr-compact-search{
          max-width:640px !important;
          min-height:48px !important;
          display:grid !important;
          grid-template-columns:22px minmax(0,1fr) auto !important;
          align-items:center !important;
          gap:8px !important;
          border:1px solid rgba(226,232,240,.96) !important;
          background:#fff !important;
          border-radius:999px !important;
          padding:5px 6px 5px 14px !important;
          box-shadow:0 14px 32px rgba(15,23,42,.055) !important;
        }

        .sr-compact-search > span{
          color:#00675f !important;
          font-size:17px !important;
          font-weight:950 !important;
        }

        .sr-compact-search input{
          height:36px !important;
          min-width:0 !important;
          border:0 !important;
          outline:0 !important;
          background:transparent !important;
          color:#07142b !important;
          font-size:13.5px !important;
          font-weight:820 !important;
        }

        .sr-compact-search input::placeholder{
          color:#94a3b8 !important;
          font-weight:760 !important;
        }

        .sr-compact-search button{
          height:38px !important;
          min-width:78px !important;
          border:0 !important;
          border-radius:999px !important;
          background:#0f172a !important;
          color:#fff !important;
          padding:0 14px !important;
          font-size:12px !important;
          font-weight:950 !important;
          cursor:pointer !important;
        }

        .sr-active-pills{
          display:flex !important;
          align-items:center !important;
          gap:7px !important;
          flex-wrap:wrap !important;
          min-height:0 !important;
        }

        .sr-active-pills button{
          min-height:32px !important;
          border:1px solid rgba(0,103,95,.15) !important;
          background:#f0fdfa !important;
          color:#00675f !important;
          border-radius:999px !important;
          padding:0 6px 0 11px !important;
          display:inline-flex !important;
          align-items:center !important;
          gap:7px !important;
          font-size:11.5px !important;
          font-weight:920 !important;
          cursor:pointer !important;
        }

        .sr-active-pills strong{
          width:20px !important;
          height:20px !important;
          display:grid !important;
          place-items:center !important;
          border-radius:999px !important;
          background:rgba(0,103,95,.12) !important;
          color:#00675f !important;
          font-size:15px !important;
          line-height:1 !important;
        }

        .sr-control-bar{
          display:flex !important;
          align-items:center !important;
          gap:8px !important;
          flex-wrap:wrap !important;
          margin-top:1px !important;
        }

        .sr-control-btn,
        .sr-control-bar > button,
        .sr-control-bar .sr-filter-master-btn{
          min-height:38px !important;
          border-radius:999px !important;
          border:1px solid rgba(226,232,240,.95) !important;
          background:#fff !important;
          color:#334155 !important;
          padding:0 13px !important;
          display:inline-flex !important;
          align-items:center !important;
          gap:8px !important;
          font-size:12.5px !important;
          font-weight:930 !important;
          cursor:pointer !important;
          box-shadow:0 9px 22px rgba(15,23,42,.04) !important;
        }

        .sr-control-btn.is-active{
          background:#0f172a !important;
          border-color:#0f172a !important;
          color:#fff !important;
        }

        .sr-control-btn strong{
          min-width:20px !important;
          height:20px !important;
          border-radius:999px !important;
          background:#14b8a6 !important;
          color:#fff !important;
          display:inline-flex !important;
          align-items:center !important;
          justify-content:center !important;
          font-size:11px !important;
        }

        /* Kill old oversized sections if any remain from previous versions */
        .sr-hero,
        .sr-category-rail-wrap,
        .sr-marketplace-suggestions,
        .sr-premium-toolbar,
        .sr-active-summary,
        .sr-subservice-strip{
          display:none !important;
        }

        .sr-results-headline{
          margin:14px 0 10px !important;
          max-width:100% !important;
          padding:0 !important;
        }

        .sr-results-headline span{
          font-size:10px !important;
          letter-spacing:.14em !important;
        }

        .sr-results-headline strong{
          font-size:22px !important;
          letter-spacing:-.045em !important;
        }

        .sr-content-grid{
          width:100% !important;
          max-width:920px !important;
          margin:0 !important;
        }

        .sr-main-list{
          gap:12px !important;
        }

        .sr-mini-menu{
          display:grid !important;
          gap:6px !important;
        }

        .sr-mini-menu span{
          display:block !important;
          color:#00675f !important;
          font-size:10.5px !important;
          font-weight:950 !important;
          letter-spacing:.14em !important;
          text-transform:uppercase !important;
          margin-bottom:4px !important;
        }

        .sr-mini-menu button{
          width:100% !important;
          min-height:40px !important;
          border:1px solid transparent !important;
          background:transparent !important;
          color:#334155 !important;
          border-radius:14px !important;
          padding:0 10px !important;
          text-align:left !important;
          font-size:13px !important;
          font-weight:850 !important;
          cursor:pointer !important;
        }

        .sr-mini-menu button:hover{
          background:#f8fafc !important;
        }

        .sr-mini-menu button.is-selected{
          background:#f0fdfa !important;
          border-color:rgba(0,103,95,.16) !important;
          color:#00675f !important;
          font-weight:950 !important;
        }

        .sr-mini-menu em{
          color:#94a3b8 !important;
          font-size:12px !important;
          font-style:normal !important;
          font-weight:800 !important;
          padding:8px 2px !important;
        }

        @media (min-width:1280px){
          .sr-shell{
            transform:translateX(-28px) !important;
          }
        }

        @media (max-width:760px){
          .sr-shell{
            width:100% !important;
            padding:12px 12px calc(92px + env(safe-area-inset-bottom,0px)) !important;
            transform:none !important;
          }

          .sr-compact-top{
            gap:9px !important;
            padding-top:8px !important;
          }

          .sr-compact-row{
            align-items:flex-start !important;
          }

          .sr-compact-title{
            flex-direction:column-reverse !important;
            align-items:flex-end !important;
            gap:5px !important;
          }

          .sr-compact-title h1{
            max-width:68vw !important;
            font-size:24px !important;
          }

          .sr-compact-search{
            max-width:none !important;
            grid-template-columns:20px minmax(0,1fr) !important;
            border-radius:20px !important;
            padding:8px 11px !important;
          }

          .sr-compact-search button{
            grid-column:1 / -1 !important;
            width:100% !important;
            height:40px !important;
            margin-top:2px !important;
          }

          .sr-control-bar{
            flex-wrap:nowrap !important;
            overflow-x:auto !important;
            scrollbar-width:none !important;
            padding-bottom:2px !important;
          }

          .sr-control-bar::-webkit-scrollbar{
            display:none !important;
          }

          .sr-control-bar > button,
          .sr-control-btn{
            flex:0 0 auto !important;
          }

          .sr-active-pills{
            flex-wrap:nowrap !important;
            overflow-x:auto !important;
            scrollbar-width:none !important;
          }

          .sr-active-pills::-webkit-scrollbar{
            display:none !important;
          }

          .sr-active-pills button{
            flex:0 0 auto !important;
          }

          .sr-content-grid{
            max-width:none !important;
          }
        }



        /* =========================================================
           LOOP SEARCH RESULTS — V4 FILTER STUDIO
           Modal centered, richer filters: location, services, category,
           modality and trust signals.
           ========================================================= */

        .sr-filter-sheet-v4{
          z-index:2147483646 !important;
          align-items:center !important;
          justify-content:center !important;
          padding:clamp(18px,4vh,40px) clamp(14px,4vw,34px) !important;
          box-sizing:border-box !important;
        }

        .sr-filter-sheet-v4 .sr-filter-sheet-backdrop{
          background:rgba(15,23,42,.46) !important;
          backdrop-filter:blur(16px) saturate(1.05) !important;
          -webkit-backdrop-filter:blur(16px) saturate(1.05) !important;
        }

        .sr-filter-sheet-v4 .sr-filter-sheet-panel{
          width:min(100%,860px) !important;
          max-height:min(86vh,760px) !important;
          border-radius:30px !important;
          padding:20px !important;
          overflow:auto !important;
          box-shadow:0 34px 100px rgba(15,23,42,.32) !important;
          border:1px solid rgba(226,232,240,.96) !important;
        }

        .sr-filter-sheet-v4 .sr-filter-sheet-head{
          padding-bottom:14px !important;
          border-bottom:1px solid rgba(226,232,240,.86) !important;
        }

        .sr-filter-sheet-v4 .sr-filter-sheet-head span{
          color:#00675f !important;
          font-size:10.5px !important;
          letter-spacing:.14em !important;
        }

        .sr-filter-sheet-v4 .sr-filter-sheet-head strong{
          font-size:24px !important;
          letter-spacing:-.055em !important;
        }

        .sr-filter-summary-card{
          margin-top:14px !important;
          border:1px solid rgba(0,103,95,.13) !important;
          background:linear-gradient(135deg,#f0fdfa,#ffffff) !important;
          border-radius:22px !important;
          padding:14px !important;
        }

        .sr-filter-summary-card small{
          display:block !important;
          color:#00675f !important;
          font-size:10px !important;
          font-weight:950 !important;
          letter-spacing:.14em !important;
          text-transform:uppercase !important;
          margin-bottom:5px !important;
        }

        .sr-filter-summary-card strong{
          display:block !important;
          color:#07142b !important;
          font-size:18px !important;
          line-height:1.05 !important;
          letter-spacing:-.04em !important;
          font-weight:950 !important;
        }

        .sr-filter-summary-card p{
          margin:6px 0 0 !important;
          color:#64748b !important;
          font-size:12.8px !important;
          line-height:1.35 !important;
          font-weight:760 !important;
        }

        .sr-filter-grid-v4{
          display:grid !important;
          grid-template-columns:repeat(2,minmax(0,1fr)) !important;
          gap:12px !important;
          margin-top:14px !important;
        }

        .sr-filter-box{
          border:1px solid rgba(226,232,240,.94) !important;
          background:#fff !important;
          border-radius:22px !important;
          padding:14px !important;
          box-shadow:0 12px 32px rgba(15,23,42,.045) !important;
          min-width:0 !important;
        }

        .sr-filter-box.span-2{
          grid-column:1 / -1 !important;
        }

        .sr-filter-box-head{
          display:flex !important;
          align-items:flex-end !important;
          justify-content:space-between !important;
          gap:12px !important;
          margin-bottom:10px !important;
        }

        .sr-filter-box-head span{
          color:#07142b !important;
          font-size:14px !important;
          font-weight:950 !important;
          letter-spacing:-.025em !important;
        }

        .sr-filter-box-head small{
          color:#94a3b8 !important;
          font-size:11px !important;
          font-weight:900 !important;
          max-width:50% !important;
          white-space:nowrap !important;
          overflow:hidden !important;
          text-overflow:ellipsis !important;
        }

        .sr-filter-sheet-v4 .sr-sheet-options.compact{
          display:grid !important;
          grid-template-columns:repeat(2,minmax(0,1fr)) !important;
          gap:8px !important;
        }

        .sr-filter-sheet-v4 .sr-sheet-options.compact button,
        .sr-service-filter-cloud button{
          min-height:42px !important;
          border:1px solid rgba(226,232,240,.96) !important;
          background:#fff !important;
          color:#334155 !important;
          border-radius:15px !important;
          padding:0 11px !important;
          text-align:left !important;
          font-size:12.8px !important;
          font-weight:900 !important;
          box-shadow:none !important;
          cursor:pointer !important;
          transition:background .14s ease,border-color .14s ease,color .14s ease,box-shadow .14s ease !important;
        }

        .sr-filter-sheet-v4 .sr-sheet-options.compact button:hover,
        .sr-service-filter-cloud button:hover{
          background:#f8fafc !important;
        }

        .sr-filter-sheet-v4 .sr-sheet-options.compact button.active,
        .sr-service-filter-cloud button.active{
          background:#f0fdfa !important;
          border-color:#99f6e4 !important;
          color:#00675f !important;
          box-shadow:0 0 0 4px rgba(20,184,166,.10) !important;
        }

        .sr-service-filter-cloud{
          display:flex !important;
          flex-wrap:wrap !important;
          gap:8px !important;
          max-height:154px !important;
          overflow:auto !important;
          padding:2px !important;
        }

        .sr-service-filter-cloud button{
          flex:0 0 auto !important;
          border-radius:999px !important;
          min-height:38px !important;
          max-width:100% !important;
          white-space:nowrap !important;
        }

        .sr-service-filter-cloud.category-cloud{
          max-height:none !important;
        }

        .sr-service-filter-cloud em{
          color:#94a3b8 !important;
          font-size:12px !important;
          font-style:normal !important;
          font-weight:800 !important;
        }

        .sr-filter-sheet-v4 .sr-filter-sheet-actions{
          margin-top:16px !important;
          position:sticky !important;
          bottom:-20px !important;
          background:linear-gradient(180deg,rgba(255,255,255,0),#fff 20%) !important;
          padding-top:18px !important;
          display:grid !important;
          grid-template-columns:1fr 1.12fr !important;
          gap:12px !important;
        }

        .sr-filter-sheet-v4 .sr-filter-sheet-actions button{
          min-height:50px !important;
          border-radius:18px !important;
          font-size:13.5px !important;
        }

        @media(max-width:760px){
          .sr-filter-sheet-v4{
            align-items:flex-end !important;
            padding:0 !important;
          }

          .sr-filter-sheet-v4 .sr-filter-sheet-panel{
            width:100% !important;
            max-height:88vh !important;
            border-radius:28px 28px 0 0 !important;
            padding:18px 14px calc(18px + env(safe-area-inset-bottom,0px)) !important;
          }

          .sr-filter-grid-v4{
            grid-template-columns:1fr !important;
          }

          .sr-filter-box.span-2{
            grid-column:auto !important;
          }

          .sr-filter-sheet-v4 .sr-filter-sheet-actions{
            grid-template-columns:1fr !important;
            bottom:calc(-18px - env(safe-area-inset-bottom,0px)) !important;
          }
        }



        /* =========================================================
           LOOP FILTER MODAL — PORTAL HARDFIX
           Fixes the modal being centered inside the page/content column
           instead of the real viewport.
           ========================================================= */

        .sr-filter-sheet-portal-hardfix{
          position:fixed !important;
          inset:0 !important;
          width:100vw !important;
          height:100dvh !important;
          min-height:100dvh !important;
          z-index:2147483646 !important;
          display:flex !important;
          align-items:center !important;
          justify-content:center !important;
          padding:clamp(18px,4vh,44px) clamp(16px,4vw,44px) !important;
          box-sizing:border-box !important;
          pointer-events:auto !important;
          transform:none !important;
          contain:none !important;
          isolation:isolate !important;
        }

        .sr-filter-sheet-portal-hardfix .sr-filter-sheet-backdrop{
          position:fixed !important;
          inset:0 !important;
          width:100vw !important;
          height:100dvh !important;
          border:0 !important;
          background:rgba(15,23,42,.48) !important;
          backdrop-filter:blur(16px) saturate(1.04) !important;
          -webkit-backdrop-filter:blur(16px) saturate(1.04) !important;
          cursor:pointer !important;
        }

        .sr-filter-sheet-portal-hardfix .sr-filter-sheet-panel{
          position:relative !important;
          margin:0 auto !important;
          width:min(100%,900px) !important;
          max-width:900px !important;
          max-height:min(86dvh,780px) !important;
          overflow:auto !important;
          border-radius:30px !important;
          background:#fff !important;
          box-shadow:0 34px 110px rgba(15,23,42,.34) !important;
          z-index:1 !important;
          transform:none !important;
        }

        @media(max-width:760px){
          .sr-filter-sheet-portal-hardfix{
            align-items:flex-end !important;
            justify-content:center !important;
            padding:0 !important;
          }

          .sr-filter-sheet-portal-hardfix .sr-filter-sheet-panel{
            width:100% !important;
            max-width:none !important;
            max-height:88dvh !important;
            border-radius:28px 28px 0 0 !important;
          }
        }



        /* =========================================================
           LOOP FILTER STUDIO V5 — category first, order outside
           ========================================================= */

        .sr-filter-sheet-v5 .sr-filter-sheet-panel{
          width:min(100%,820px) !important;
          max-width:820px !important;
          padding:18px !important;
        }

        .sr-filter-sheet-v5 .sr-filter-sheet-head{
          padding-bottom:12px !important;
        }

        .sr-filter-sheet-v5 .sr-filter-sheet-head strong{
          font-size:22px !important;
        }

        .sr-filter-summary-compact{
          margin-top:12px !important;
          padding:12px 14px !important;
          border-radius:20px !important;
          background:linear-gradient(135deg,#f0fdfa,#ffffff) !important;
        }

        .sr-filter-summary-compact strong{
          font-size:16px !important;
        }

        .sr-filter-summary-compact p{
          font-size:12.2px !important;
        }

        .sr-filter-grid-v5{
          gap:10px !important;
          margin-top:12px !important;
        }

        .sr-filter-sheet-v5 .sr-filter-box{
          padding:13px !important;
          border-radius:20px !important;
          box-shadow:0 10px 28px rgba(15,23,42,.038) !important;
        }

        .sr-filter-sheet-v5 .sr-filter-box-head{
          margin-bottom:9px !important;
        }

        .sr-filter-sheet-v5 .sr-filter-box-head span{
          font-size:13.5px !important;
        }

        .sr-filter-sheet-v5 .sr-filter-box-head small{
          font-size:10.8px !important;
        }

        .sr-filter-priority-box{
          background:linear-gradient(135deg,#ffffff,#fbfffd) !important;
          border-color:rgba(0,103,95,.13) !important;
        }

        .sr-filter-search-row{
          min-height:44px !important;
          border:1px solid rgba(226,232,240,.96) !important;
          background:#fff !important;
          border-radius:999px !important;
          display:grid !important;
          grid-template-columns:22px minmax(0,1fr) auto !important;
          align-items:center !important;
          gap:8px !important;
          padding:0 10px 0 13px !important;
          margin-bottom:10px !important;
        }

        .sr-filter-search-row span{
          color:#00675f !important;
          font-weight:950 !important;
          font-size:15px !important;
        }

        .sr-filter-search-row input{
          border:0 !important;
          outline:0 !important;
          background:transparent !important;
          min-width:0 !important;
          height:38px !important;
          color:#0f172a !important;
          font-size:13px !important;
          font-weight:820 !important;
        }

        .sr-filter-search-row input::placeholder{
          color:#94a3b8 !important;
          font-weight:760 !important;
        }

        .sr-filter-search-row button{
          width:26px !important;
          height:26px !important;
          border:0 !important;
          border-radius:999px !important;
          background:#f1f5f9 !important;
          color:#64748b !important;
          display:grid !important;
          place-items:center !important;
          font-size:16px !important;
          font-weight:950 !important;
          cursor:pointer !important;
        }

        .sr-location-filter-list{
          display:flex !important;
          flex-wrap:wrap !important;
          gap:8px !important;
          max-height:118px !important;
          overflow:auto !important;
          padding:1px !important;
        }

        .sr-location-filter-list button{
          min-height:38px !important;
          max-width:100% !important;
          border:1px solid rgba(226,232,240,.96) !important;
          background:#fff !important;
          color:#334155 !important;
          border-radius:999px !important;
          padding:0 12px !important;
          font-size:12.5px !important;
          font-weight:900 !important;
          cursor:pointer !important;
          white-space:nowrap !important;
          overflow:hidden !important;
          text-overflow:ellipsis !important;
        }

        .sr-location-filter-list button.active{
          background:#f0fdfa !important;
          border-color:#99f6e4 !important;
          color:#00675f !important;
          box-shadow:0 0 0 4px rgba(20,184,166,.10) !important;
        }

        .sr-service-filter-cloud-v5{
          max-height:112px !important;
        }

        .sr-filter-sheet-v5 .sr-sheet-options.compact button{
          min-height:40px !important;
          border-radius:999px !important;
        }

        .sr-filter-sheet-v5 .sr-filter-sheet-actions{
          margin-top:14px !important;
          padding-top:16px !important;
        }

        .sr-filter-sheet-v5 .sr-filter-sheet-actions button{
          min-height:48px !important;
          border-radius:18px !important;
        }

        @media(max-width:760px){
          .sr-filter-sheet-v5 .sr-filter-sheet-panel{
            padding:16px 13px calc(16px + env(safe-area-inset-bottom,0px)) !important;
          }

          .sr-filter-grid-v5{
            grid-template-columns:1fr !important;
          }

          .sr-location-filter-list,
          .sr-service-filter-cloud-v5{
            flex-wrap:nowrap !important;
            overflow-x:auto !important;
            overflow-y:hidden !important;
            max-height:none !important;
            scrollbar-width:none !important;
          }

          .sr-location-filter-list::-webkit-scrollbar,
          .sr-service-filter-cloud-v5::-webkit-scrollbar{
            display:none !important;
          }

          .sr-location-filter-list button,
          .sr-service-filter-cloud-v5 button{
            flex:0 0 auto !important;
          }
        }



        /* =========================================================
           LOOP SEARCH RESULTS — BUSINESS CARDS WIDTH + LOGO POLISH
           Uses more width and lets BusinessResultCard show SmartLogoFrame correctly.
           ========================================================= */

        .sr-shell{
          width:min(100%,1180px) !important;
          max-width:1180px !important;
          margin:0 auto !important;
          padding-left:clamp(18px,3vw,36px) !important;
          padding-right:clamp(18px,3vw,36px) !important;
        }

        .sr-content-grid{
          width:100% !important;
          max-width:1040px !important;
          margin:0 !important;
        }

        .sr-results-headline{
          width:100% !important;
          max-width:1040px !important;
        }

        .sr-main-list{
          width:100% !important;
          max-width:1040px !important;
        }

        .sr-main-list .brc-card{
          width:100% !important;
        }

        .sr-main-list .brc-media{
          background:#f8fafc !important;
        }

        .sr-main-list .brc-logo-frame{
          background:#f8fafc !important;
        }

        .sr-main-list .smart-logo-inner{
          padding:0 !important;
        }

        @media (min-width:1280px){
          .sr-shell{
            transform:translateX(-12px) !important;
          }
        }

        @media (max-width:760px){
          .sr-shell,
          .sr-content-grid,
          .sr-results-headline,
          .sr-main-list{
            max-width:none !important;
            width:100% !important;
            transform:none !important;
          }
        }



        /* SEARCH RESULT LOGO RATIO FIX — keep business logos fully visible */
        .sr-main-list .brc-card{
          grid-template-columns:220px minmax(0,1fr) !important;
        }

        .sr-main-list .brc-media{
          height:138px !important;
          aspect-ratio:220 / 138 !important;
          background:#f8fafc !important;
        }

        .sr-main-list .brc-logo-frame .smart-logo-inner{
          padding:10px 14px !important;
        }

        .sr-main-list .brc-logo-frame .smart-logo-img{
          object-fit:contain !important;
        }

        @media(max-width:900px){
          .sr-main-list .brc-card{
            grid-template-columns:180px minmax(0,1fr) !important;
          }
          .sr-main-list .brc-media{
            height:120px !important;
            aspect-ratio:180 / 120 !important;
          }
        }

        @media(max-width:720px){
          .sr-main-list .brc-card{
            grid-template-columns:116px minmax(0,1fr) !important;
          }
          .sr-main-list .brc-media{
            height:92px !important;
            aspect-ratio:116 / 92 !important;
          }
          .sr-main-list .brc-logo-frame .smart-logo-inner{
            padding:7px 9px !important;
          }
        }



        /* =========================================================
           LOOP SEARCH V6 — Encuentra24-inspired compact marketplace
           One global search, compact filter row, sidebar filter modal.
           ========================================================= */

        .sr-marketplace-top{
          padding:10px 0 12px !important;
          gap:10px !important;
          border-bottom:1px solid rgba(226,232,240,.84) !important;
        }

        .sr-marketplace-title-row{
          display:flex !important;
          align-items:center !important;
          justify-content:space-between !important;
          gap:14px !important;
        }

        .sr-marketplace-heading{
          min-width:0 !important;
          flex:1 !important;
          display:flex !important;
          align-items:center !important;
          justify-content:flex-end !important;
          gap:10px !important;
        }

        .sr-marketplace-heading span{
          flex:0 0 auto !important;
          min-height:30px !important;
          display:inline-flex !important;
          align-items:center !important;
          padding:0 12px !important;
          border-radius:999px !important;
          background:#ecfdf5 !important;
          border:1px solid rgba(0,103,95,.14) !important;
          color:#00675f !important;
          font-size:11.5px !important;
          font-weight:950 !important;
          white-space:nowrap !important;
        }

        .sr-marketplace-heading h1{
          margin:0 !important;
          color:#07142b !important;
          font-size:clamp(24px,2.7vw,36px) !important;
          line-height:1 !important;
          letter-spacing:-.06em !important;
          font-weight:950 !important;
          white-space:nowrap !important;
          overflow:hidden !important;
          text-overflow:ellipsis !important;
        }

        .sr-marketplace-filter-row{
          display:grid !important;
          grid-template-columns:minmax(0,1fr) auto !important;
          gap:10px !important;
          align-items:center !important;
        }

        .sr-active-query-pills{
          min-width:0 !important;
          flex-wrap:nowrap !important;
          overflow-x:auto !important;
          scrollbar-width:none !important;
        }

        .sr-active-query-pills::-webkit-scrollbar{
          display:none !important;
        }

        .sr-active-query-pills button{
          flex:0 0 auto !important;
        }

        .sr-marketplace-controls{
          justify-content:flex-end !important;
          flex-wrap:nowrap !important;
          overflow-x:auto !important;
          scrollbar-width:none !important;
        }

        .sr-marketplace-controls::-webkit-scrollbar{
          display:none !important;
        }

        .sr-marketplace-controls > button,
        .sr-marketplace-controls .sr-control-btn{
          flex:0 0 auto !important;
        }

        .sr-control-btn.has-value{
          border-color:#99f6e4 !important;
          background:#f0fdfa !important;
          color:#00675f !important;
        }

        /* Hide the old duplicated internal search form if a previous build leaves it around */
        .sr-compact-search{
          display:none !important;
        }

        .sr-filter-sheet-e24{
          position:fixed !important;
          inset:0 !important;
          width:100vw !important;
          height:100dvh !important;
          display:flex !important;
          align-items:center !important;
          justify-content:center !important;
          padding:clamp(18px,4vh,40px) clamp(14px,4vw,40px) !important;
          z-index:2147483646 !important;
          box-sizing:border-box !important;
          isolation:isolate !important;
        }

        .sr-filter-sheet-e24 .sr-filter-sheet-backdrop{
          position:fixed !important;
          inset:0 !important;
          background:rgba(15,23,42,.50) !important;
          backdrop-filter:blur(14px) saturate(1.03) !important;
          -webkit-backdrop-filter:blur(14px) saturate(1.03) !important;
          border:0 !important;
        }

        .sr-filter-sheet-e24 .sr-filter-sheet-panel{
          position:relative !important;
          width:min(100%,900px) !important;
          max-width:900px !important;
          height:min(82dvh,720px) !important;
          max-height:min(82dvh,720px) !important;
          display:grid !important;
          grid-template-rows:auto minmax(0,1fr) auto !important;
          padding:0 !important;
          overflow:hidden !important;
          background:#fff !important;
          border:1px solid rgba(226,232,240,.92) !important;
          border-radius:22px !important;
          box-shadow:0 34px 110px rgba(15,23,42,.35) !important;
          z-index:1 !important;
        }

        .sr-e24-head{
          min-height:64px !important;
          padding:0 20px !important;
          border-bottom:1px solid rgba(226,232,240,.90) !important;
          display:flex !important;
          align-items:center !important;
          justify-content:space-between !important;
        }

        .sr-e24-head span{
          display:block !important;
          color:#00675f !important;
          font-size:10px !important;
          font-weight:950 !important;
          letter-spacing:.16em !important;
          text-transform:uppercase !important;
          margin-bottom:4px !important;
        }

        .sr-e24-head strong{
          font-size:22px !important;
          line-height:1 !important;
          letter-spacing:-.04em !important;
        }

        .sr-e24-head > button{
          width:42px !important;
          height:42px !important;
          border-radius:999px !important;
          border:1px solid rgba(226,232,240,.95) !important;
          background:#fff !important;
          color:#334155 !important;
          font-size:22px !important;
          font-weight:800 !important;
          display:grid !important;
          place-items:center !important;
        }

        .sr-e24-layout{
          min-height:0 !important;
          display:grid !important;
          grid-template-columns:230px minmax(0,1fr) !important;
          overflow:hidden !important;
        }

        .sr-e24-sidebar{
          background:#f8fafc !important;
          border-right:1px solid rgba(226,232,240,.88) !important;
          overflow:auto !important;
          padding:0 !important;
        }

        .sr-e24-sidebar button{
          width:100% !important;
          min-height:54px !important;
          border:0 !important;
          border-bottom:1px solid rgba(226,232,240,.80) !important;
          background:transparent !important;
          color:#475569 !important;
          display:flex !important;
          align-items:center !important;
          justify-content:space-between !important;
          gap:12px !important;
          padding:0 18px !important;
          font-size:14px !important;
          font-weight:820 !important;
          text-align:left !important;
          cursor:pointer !important;
          border-radius:0 !important;
        }

        .sr-e24-sidebar button.is-current{
          background:#fff !important;
          color:#00675f !important;
          box-shadow:inset 4px 0 0 #00675f !important;
          font-weight:950 !important;
        }

        .sr-e24-sidebar button strong{
          color:#00675f !important;
          font-size:13px !important;
          font-weight:950 !important;
        }

        .sr-e24-content{
          min-height:0 !important;
          overflow:auto !important;
          padding:26px !important;
        }

        .sr-e24-section h3{
          margin:0 0 8px !important;
          color:#0f172a !important;
          font-size:22px !important;
          line-height:1.08 !important;
          letter-spacing:-.035em !important;
          font-weight:950 !important;
        }

        .sr-e24-section p{
          margin:0 0 18px !important;
          color:#64748b !important;
          font-size:13px !important;
          line-height:1.45 !important;
          font-weight:750 !important;
        }

        .sr-e24-input{
          min-height:50px !important;
          border:1px solid rgba(226,232,240,.96) !important;
          border-radius:999px !important;
          background:#fff !important;
          display:grid !important;
          grid-template-columns:24px minmax(0,1fr) auto !important;
          align-items:center !important;
          gap:10px !important;
          padding:0 12px 0 16px !important;
          box-shadow:0 10px 24px rgba(15,23,42,.04) !important;
        }

        .sr-e24-input span{
          color:#00675f !important;
          font-weight:950 !important;
        }

        .sr-e24-input input{
          height:46px !important;
          border:0 !important;
          outline:0 !important;
          background:transparent !important;
          color:#0f172a !important;
          font-size:14px !important;
          font-weight:800 !important;
          min-width:0 !important;
        }

        .sr-e24-input input::placeholder{
          color:#94a3b8 !important;
          font-weight:750 !important;
        }

        .sr-e24-input button{
          width:30px !important;
          height:30px !important;
          border:0 !important;
          border-radius:999px !important;
          background:#f1f5f9 !important;
          color:#64748b !important;
          font-size:18px !important;
          font-weight:900 !important;
          cursor:pointer !important;
        }

        .sr-e24-chip-grid,
        .sr-e24-location-results{
          display:flex !important;
          flex-wrap:wrap !important;
          gap:10px !important;
          align-items:flex-start !important;
        }

        .sr-e24-chip-grid button,
        .sr-e24-location-results button,
        .sr-e24-selected-location button{
          min-height:42px !important;
          border:1px solid rgba(226,232,240,.96) !important;
          background:#fff !important;
          color:#334155 !important;
          border-radius:999px !important;
          padding:0 16px !important;
          font-size:13.2px !important;
          font-weight:850 !important;
          cursor:pointer !important;
          box-shadow:0 8px 18px rgba(15,23,42,.035) !important;
        }

        .sr-e24-chip-grid button.active,
        .sr-e24-location-results button.active,
        .sr-e24-selected-location button{
          border-color:#2dd4bf !important;
          background:#f0fdfa !important;
          color:#00675f !important;
          box-shadow:0 0 0 4px rgba(20,184,166,.10) !important;
          font-weight:950 !important;
        }

        .sr-e24-selected-location{
          margin:14px 0 !important;
          display:grid !important;
          gap:8px !important;
        }

        .sr-e24-selected-location span{
          color:#00675f !important;
          font-size:10px !important;
          font-weight:950 !important;
          letter-spacing:.14em !important;
          text-transform:uppercase !important;
        }

        .sr-e24-location-results{
          margin-top:14px !important;
        }

        .sr-e24-location-results em,
        .sr-e24-chip-grid em{
          color:#94a3b8 !important;
          font-style:normal !important;
          font-weight:800 !important;
          font-size:13px !important;
        }

        .sr-e24-actions{
          height:72px !important;
          padding:10px 18px !important;
          border-top:1px solid rgba(226,232,240,.90) !important;
          display:grid !important;
          grid-template-columns:220px minmax(0,1fr) !important;
          gap:12px !important;
          background:#fff !important;
          margin:0 !important;
        }

        .sr-e24-actions button{
          min-height:50px !important;
          border-radius:999px !important;
          font-size:14px !important;
          font-weight:950 !important;
        }

        .sr-e24-actions .primary{
          background:#00675f !important;
          color:#fff !important;
          border:0 !important;
          box-shadow:0 14px 28px rgba(0,103,95,.18) !important;
        }

        .sr-e24-actions .secondary{
          background:#fff !important;
          color:#334155 !important;
          border:1px solid rgba(226,232,240,.96) !important;
        }

        @media(max-width:860px){
          .sr-marketplace-filter-row{
            grid-template-columns:1fr !important;
          }

          .sr-marketplace-heading{
            flex-direction:column-reverse !important;
            align-items:flex-end !important;
            gap:5px !important;
          }

          .sr-marketplace-heading h1{
            max-width:68vw !important;
            font-size:24px !important;
          }

          .sr-marketplace-controls{
            justify-content:flex-start !important;
            padding-bottom:2px !important;
          }

          .sr-filter-sheet-e24{
            align-items:flex-end !important;
            padding:0 !important;
          }

          .sr-filter-sheet-e24 .sr-filter-sheet-panel{
            width:100% !important;
            max-width:none !important;
            height:88dvh !important;
            max-height:88dvh !important;
            border-radius:26px 26px 0 0 !important;
          }

          .sr-e24-layout{
            grid-template-columns:1fr !important;
            grid-template-rows:auto minmax(0,1fr) !important;
          }

          .sr-e24-sidebar{
            display:flex !important;
            overflow-x:auto !important;
            overflow-y:hidden !important;
            border-right:0 !important;
            border-bottom:1px solid rgba(226,232,240,.88) !important;
            scrollbar-width:none !important;
          }

          .sr-e24-sidebar::-webkit-scrollbar{
            display:none !important;
          }

          .sr-e24-sidebar button{
            flex:0 0 auto !important;
            width:auto !important;
            min-height:48px !important;
            border-bottom:0 !important;
            border-right:1px solid rgba(226,232,240,.72) !important;
            padding:0 14px !important;
          }

          .sr-e24-sidebar button.is-current{
            box-shadow:inset 0 -3px 0 #00675f !important;
          }

          .sr-e24-content{
            padding:20px 16px !important;
          }

          .sr-e24-actions{
            height:auto !important;
            grid-template-columns:1fr 1.4fr !important;
            padding:10px 14px calc(10px + env(safe-area-inset-bottom,0px)) !important;
          }
        }



        /* =========================================================
           LOOP FILTERS E24 — COMPACT GRID RIGHT PANEL
           Uses the right side as a filter workspace instead of a single
           section with too much empty white space.
           ========================================================= */

        .sr-filter-sheet-e24 .sr-filter-sheet-panel{
          width:min(100%,980px) !important;
          max-width:980px !important;
          max-height:min(84dvh,760px) !important;
        }

        .sr-e24-layout{
          grid-template-columns:230px minmax(0,1fr) !important;
        }

        .sr-e24-content-grid{
          display:grid !important;
          grid-template-columns:repeat(2,minmax(0,1fr)) !important;
          align-content:start !important;
          gap:12px !important;
          padding:18px !important;
          overflow:auto !important;
          background:linear-gradient(135deg,#ffffff 0%,#fbfffd 100%) !important;
        }

        .sr-e24-section.sr-e24-card{
          border:1px solid rgba(226,232,240,.92) !important;
          background:#fff !important;
          border-radius:20px !important;
          padding:14px !important;
          margin:0 !important;
          min-width:0 !important;
          box-shadow:0 10px 26px rgba(15,23,42,.035) !important;
          transition:border-color .15s ease, box-shadow .15s ease, background .15s ease !important;
        }

        .sr-e24-section.sr-e24-card.is-focus{
          border-color:rgba(20,184,166,.46) !important;
          box-shadow:0 0 0 4px rgba(20,184,166,.10), 0 12px 30px rgba(15,23,42,.045) !important;
          background:linear-gradient(135deg,#ffffff,#f8fffd) !important;
        }

        .sr-e24-section.sr-e24-card.span-2{
          grid-column:1 / -1 !important;
        }

        .sr-e24-section-head{
          display:flex !important;
          justify-content:space-between !important;
          align-items:flex-start !important;
          gap:12px !important;
          margin-bottom:10px !important;
        }

        .sr-e24-section-head h3{
          margin:0 !important;
          font-size:15px !important;
          letter-spacing:-.025em !important;
          line-height:1.08 !important;
        }

        .sr-e24-section-head p{
          margin:4px 0 0 !important;
          color:#64748b !important;
          font-size:12px !important;
          line-height:1.25 !important;
          font-weight:760 !important;
        }

        .sr-e24-section-head > span{
          width:24px !important;
          height:24px !important;
          border-radius:999px !important;
          display:grid !important;
          place-items:center !important;
          background:#ecfdf5 !important;
          color:#00796f !important;
          font-size:13px !important;
          font-weight:950 !important;
          flex:0 0 auto !important;
        }

        .sr-e24-content-grid .sr-e24-input{
          min-height:42px !important;
          border-radius:16px !important;
          margin:0 !important;
        }

        .sr-e24-content-grid .sr-e24-input input{
          height:40px !important;
          font-size:12.7px !important;
        }

        .sr-e24-chip-grid.compact{
          display:flex !important;
          flex-wrap:wrap !important;
          gap:8px !important;
          margin:0 !important;
          max-height:126px !important;
          overflow:auto !important;
          padding:1px !important;
        }

        .sr-e24-chip-grid.compact.two{
          display:grid !important;
          grid-template-columns:repeat(2,minmax(0,1fr)) !important;
          max-height:none !important;
          overflow:visible !important;
        }

        .sr-e24-chip-grid.compact.order{
          display:grid !important;
          grid-template-columns:repeat(4,minmax(0,1fr)) !important;
          max-height:none !important;
          overflow:visible !important;
        }

        .sr-e24-chip-grid.compact button{
          min-height:38px !important;
          border-radius:999px !important;
          padding:0 12px !important;
          font-size:12px !important;
          line-height:1.05 !important;
          white-space:nowrap !important;
        }

        .sr-e24-chip-grid.compact.two button,
        .sr-e24-chip-grid.compact.order button{
          white-space:normal !important;
          text-align:center !important;
        }

        .sr-e24-selected-location{
          margin-top:10px !important;
          padding:9px 10px !important;
          border-radius:16px !important;
        }

        .sr-e24-location-results{
          margin-top:10px !important;
          display:flex !important;
          flex-wrap:wrap !important;
          gap:8px !important;
          max-height:110px !important;
          overflow:auto !important;
        }

        .sr-e24-location-results button{
          min-height:36px !important;
          border-radius:999px !important;
          padding:0 11px !important;
          white-space:nowrap !important;
        }

        @media(max-width:860px){
          .sr-e24-content-grid{
            grid-template-columns:1fr !important;
            padding:14px !important;
          }

          .sr-e24-section.sr-e24-card.span-2{
            grid-column:auto !important;
          }

          .sr-e24-chip-grid.compact.order{
            grid-template-columns:repeat(2,minmax(0,1fr)) !important;
          }
        }

        @media(max-width:640px){
          .sr-filter-sheet-e24 .sr-filter-sheet-panel{
            width:100% !important;
            max-width:none !important;
            max-height:88dvh !important;
          }

          .sr-e24-layout{
            grid-template-columns:1fr !important;
          }

          .sr-e24-content-grid{
            padding:12px !important;
          }
        }



        /* =========================================================
           LOOP SEARCH RESULTS — CLEANER MARKETPLACE TOP BAR
           Quita el título duplicado, deja un solo botón de filtros,
           mantiene orden fuera y baja el ruido visual.
           ========================================================= */

        .sr-search-top-clean{
          max-width:960px !important;
          margin:0 !important;
          padding:16px 0 14px !important;
          border-bottom:1px solid rgba(226,232,240,.88) !important;
          display:grid !important;
          gap:12px !important;
        }

        .sr-search-topline-clean{
          display:flex !important;
          align-items:center !important;
          justify-content:space-between !important;
          gap:12px !important;
        }

        .sr-search-status-pill{
          min-height:34px !important;
          padding:0 13px !important;
          border-radius:999px !important;
          display:inline-flex !important;
          align-items:center !important;
          justify-content:center !important;
          background:rgba(236,253,245,.86) !important;
          border:1px solid rgba(20,184,166,.22) !important;
          color:#00675f !important;
          font-size:12px !important;
          font-weight:950 !important;
          white-space:nowrap !important;
          box-shadow:0 8px 20px rgba(15,23,42,.035) !important;
        }

        .sr-filter-row-clean{
          display:flex !important;
          align-items:center !important;
          justify-content:space-between !important;
          gap:12px !important;
          margin:0 !important;
          padding:0 !important;
        }

        .sr-filter-row-clean .sr-active-query-pills{
          flex:1 1 auto !important;
          min-width:0 !important;
          display:flex !important;
          align-items:center !important;
          gap:8px !important;
          overflow-x:auto !important;
          scrollbar-width:none !important;
          -ms-overflow-style:none !important;
          padding:0 !important;
        }

        .sr-filter-row-clean .sr-active-query-pills::-webkit-scrollbar{ display:none !important; }

        .sr-filter-row-clean .sr-active-query-pills button{
          min-height:34px !important;
          padding:0 10px 0 12px !important;
          border-radius:999px !important;
          background:rgba(236,253,245,.82) !important;
          border:1px solid rgba(20,184,166,.20) !important;
          color:#00675f !important;
          font-size:11.7px !important;
          font-weight:950 !important;
          box-shadow:none !important;
          max-width:220px !important;
        }

        .sr-filter-row-clean .sr-active-query-pills button span{
          overflow:hidden !important;
          text-overflow:ellipsis !important;
          white-space:nowrap !important;
        }

        .sr-filter-row-clean .sr-active-query-pills button strong{
          min-width:20px !important;
          height:20px !important;
          display:grid !important;
          place-items:center !important;
          border-radius:999px !important;
          background:rgba(0,103,95,.10) !important;
          color:#00675f !important;
          font-size:13px !important;
          line-height:1 !important;
        }

        .sr-controls-clean{
          flex:0 0 auto !important;
          display:flex !important;
          align-items:center !important;
          gap:8px !important;
          padding:0 !important;
          background:transparent !important;
          box-shadow:none !important;
          border:0 !important;
        }

        .sr-controls-clean .sr-control-btn,
        .sr-controls-clean > button,
        .sr-controls-clean .sr-filter-icon-btn{
          min-height:40px !important;
          height:40px !important;
          border-radius:999px !important;
          padding:0 13px !important;
          border:1px solid rgba(226,232,240,.96) !important;
          background:#fff !important;
          color:#334155 !important;
          font-size:12.4px !important;
          font-weight:950 !important;
          box-shadow:0 10px 24px rgba(15,23,42,.055) !important;
        }

        .sr-controls-clean .sr-control-btn.is-active,
        .sr-controls-clean .sr-filter-icon-btn.is-active,
        .sr-controls-clean > button[aria-label="Ordenar resultados"]{
          border-color:rgba(0,103,95,.18) !important;
        }

        .sr-filter-icon-btn{
          gap:9px !important;
        }

        .sr-filter-icon-btn strong{
          min-width:22px !important;
          height:22px !important;
          border-radius:999px !important;
          display:grid !important;
          place-items:center !important;
          background:#00675f !important;
          color:#fff !important;
          font-size:11px !important;
          font-weight:950 !important;
        }

        .sr-filter-sliders{
          width:18px !important;
          height:16px !important;
          display:grid !important;
          gap:4px !important;
          align-items:center !important;
        }

        .sr-filter-sliders i{
          position:relative !important;
          display:block !important;
          height:2px !important;
          border-radius:999px !important;
          background:#00675f !important;
        }

        .sr-filter-sliders i::after{
          content:"" !important;
          position:absolute !important;
          top:50% !important;
          width:5px !important;
          height:5px !important;
          border-radius:999px !important;
          background:#00675f !important;
          transform:translateY(-50%) !important;
        }

        .sr-filter-sliders i:nth-child(1)::after{ left:3px !important; }
        .sr-filter-sliders i:nth-child(2)::after{ right:2px !important; }
        .sr-filter-sliders i:nth-child(3)::after{ left:8px !important; }

        .sr-results-headline{
          max-width:960px !important;
          margin-top:16px !important;
        }

        .sr-main-list{
          max-width:960px !important;
        }

        @media(max-width:760px){
          .sr-search-top-clean{
            padding:12px 0 12px !important;
            gap:10px !important;
          }

          .sr-filter-row-clean{
            align-items:flex-start !important;
            flex-direction:column !important;
          }

          .sr-controls-clean{
            width:100% !important;
            overflow-x:auto !important;
            padding-bottom:2px !important;
          }

          .sr-controls-clean .sr-filter-icon-btn{
            flex:1 0 auto !important;
          }

          .sr-controls-clean > button{
            flex:1 0 auto !important;
          }
        }

        @media(max-width:520px){
          .sr-search-topline-clean{
            align-items:flex-start !important;
          }

          .sr-filter-row-clean .sr-active-query-pills button{
            max-width:180px !important;
          }

          .sr-controls-clean{
            gap:7px !important;
          }

          .sr-controls-clean .sr-control-btn,
          .sr-controls-clean > button,
          .sr-controls-clean .sr-filter-icon-btn{
            height:38px !important;
            min-height:38px !important;
            padding:0 11px !important;
            font-size:12px !important;
          }
        }




        /* =========================================================
           LOOP FILTERS — SIMPLIFIED FILTER MODAL
           - Orden queda fuera del modal.
           - Disponibilidad se retira del modal.
           - Palabra clave / Categoría full-width para bajar ruido visual.
           - Modalidad se muestra como chips horizontales tipo Servicios.
           ========================================================= */

        .sr-e24-sidebar button:has(span){
          min-height:54px !important;
        }

        .sr-filter-sheet-e24 .sr-e24-sidebar{
          grid-template-rows:auto !important;
        }

        .sr-e24-content-grid{
          grid-template-columns:repeat(2,minmax(0,1fr)) !important;
          gap:12px !important;
        }

        .sr-e24-section.sr-e24-card.span-2{
          grid-column:1 / -1 !important;
        }

        .sr-e24-section.sr-e24-card.span-2 .sr-e24-chip-grid.compact{
          max-height:none !important;
        }

        .sr-e24-chip-grid.compact.service-like{
          display:flex !important;
          flex-wrap:wrap !important;
          gap:8px !important;
          max-height:none !important;
          overflow:visible !important;
          padding:1px !important;
        }

        .sr-e24-chip-grid.compact.service-like button{
          flex:0 0 auto !important;
          min-width:126px !important;
          min-height:38px !important;
          border-radius:999px !important;
          white-space:nowrap !important;
          text-align:center !important;
        }

        .sr-e24-section-head p{
          max-width:560px !important;
        }

        @media(max-width:860px){
          .sr-e24-content-grid{
            grid-template-columns:1fr !important;
          }

          .sr-e24-section.sr-e24-card.span-2{
            grid-column:auto !important;
          }

          .sr-e24-chip-grid.compact.service-like button{
            min-width:auto !important;
          }
        }



        /* =========================================================
           LOOP SERVICE RESULTS — PREMIUM MOBILE RESULT CARD CTA V52
           Search/list result cards: softer card, premium reserve pill,
           more intentional top-right placement.
           ========================================================= */
        @media (max-width: 720px) {
          .sr-main-list {
            display: grid !important;
            gap: 12px !important;
          }

          .sr-main-list > .sr-result-card-v53,
          .sr-featured-card-grid > .sr-result-card-v53,
          .sr-featured-grid > .sr-result-card-v53 {
            position: relative !important;
            overflow: hidden !important;
            border-radius: 24px !important;
            border: 1px solid rgba(204, 251, 241, .68) !important;
            background:
              radial-gradient(circle at 92% 0%, rgba(0, 137, 127, .08), transparent 34%),
              rgba(255, 255, 255, .94) !important;
            box-shadow: 0 16px 38px rgba(54, 41, 25, .075) !important;
            min-height: 112px !important;
            isolation: isolate !important;
          }

          .sr-main-list > .sr-result-card-v53::after,
          .sr-featured-card-grid > .sr-result-card-v53::after,
          .sr-featured-grid > .sr-result-card-v53::after {
            content: "" !important;
            position: absolute !important;
            inset: 0 !important;
            pointer-events: none !important;
            border-radius: inherit !important;
            box-shadow: inset 0 1px 0 rgba(255,255,255,.78) !important;
            z-index: 1 !important;
          }

          .sr-result-card-v53 .sr-result-reserve-cta-v53 {
            position: absolute !important;
            top: 10px !important;
            right: 10px !important;
            z-index: 22 !important;

            width: auto !important;
            min-width: 82px !important;
            max-width: 104px !important;
            height: 34px !important;
            min-height: 34px !important;
            max-height: 34px !important;
            padding: 0 13px !important;

            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 6px !important;

            border: 0 !important;
            border-radius: 999px !important;
            background:
              radial-gradient(circle at 20% 0%, rgba(255,255,255,.22), transparent 36%),
              linear-gradient(135deg, #00675f 0%, #008878 100%) !important;
            color: #ffffff !important;
            box-shadow:
              0 13px 26px rgba(0, 103, 95, .24),
              inset 0 1px 0 rgba(255,255,255,.22) !important;

            font-size: 11.5px !important;
            line-height: 1 !important;
            font-weight: 950 !important;
            letter-spacing: -.01em !important;
            white-space: nowrap !important;
            transform: translateY(-1px) !important;
          }

          .sr-result-card-v53 .sr-result-reserve-cta-v53:active {
            transform: translateY(0) scale(.985) !important;
          }

          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="content"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="Content"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="copy"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="Copy"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="body"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="Body"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="info"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="Info"] {
            padding-right: 92px !important;
            box-sizing: border-box !important;
          }

          .sr-result-card-v53 img {
            border-radius: 18px !important;
          }

          .sr-result-card-v53 [class*="badge"],
          .sr-result-card-v53 [class*="Badge"],
          .sr-result-card-v53 [class*="chip"],
          .sr-result-card-v53 [class*="Chip"] {
            border-radius: 999px !important;
          }
        }

        @media (max-width: 390px) {
          .sr-result-card-v53 .sr-result-reserve-cta-v53 {
            top: 9px !important;
            right: 9px !important;
            min-width: 76px !important;
            max-width: 96px !important;
            height: 32px !important;
            min-height: 32px !important;
            max-height: 32px !important;
            padding: 0 11px !important;
            font-size: 11px !important;
          }

          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="content"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="Content"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="copy"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="Copy"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="body"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="Body"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="info"],
          .sr-result-card-v53:has(.sr-result-reserve-cta-v53) [class*="Info"] {
            padding-right: 84px !important;
          }
        }



        /* =========================================================
           LOOP SERVICE RESULTS — COMPACT PREMIUM MOBILE CARDS V53
           - Smaller top-right reserve CTA.
           - Prevents business name/location overlap.
           - Hides "Servicio principal".
           - Makes remaining meta chips smaller and inline.
           ========================================================= */
        @media (max-width: 720px) {
          .sr-main-list {
            gap: 10px !important;
          }

          .sr-main-list > .sr-result-card-v53,
          .sr-featured-card-grid > .sr-result-card-v53,
          .sr-featured-grid > .sr-result-card-v53,
          .sr-main-list .brc-card.sr-result-card-v53 {
            min-height: 100px !important;
            border-radius: 23px !important;
            border: 1px solid rgba(204, 251, 241, .72) !important;
            background:
              radial-gradient(circle at 96% 0%, rgba(0, 137, 127, .065), transparent 30%),
              rgba(255, 255, 255, .94) !important;
            box-shadow: 0 13px 30px rgba(54, 41, 25, .065) !important;
            overflow: hidden !important;
          }

          .sr-result-card-v53 .sr-result-reserve-cta-v53 {
            position: absolute !important;
            top: 9px !important;
            right: 10px !important;
            z-index: 28 !important;

            min-width: 66px !important;
            max-width: 78px !important;
            width: auto !important;
            height: 29px !important;
            min-height: 29px !important;
            max-height: 29px !important;
            padding: 0 10px !important;

            border: 0 !important;
            border-radius: 999px !important;
            background: linear-gradient(135deg, #00796f 0%, #009080 100%) !important;
            color: #ffffff !important;
            box-shadow: 0 10px 20px rgba(0, 103, 95, .20) !important;

            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;

            font-size: 10.5px !important;
            line-height: 1 !important;
            font-weight: 950 !important;
            letter-spacing: -.01em !important;
            white-space: nowrap !important;
            transform: none !important;
          }

          .sr-result-card-v53 .sr-result-reserve-cta-v53:active {
            transform: scale(.985) !important;
          }

          /* Reserve a compact safe lane only for the top text, not the whole card. */
          .sr-result-card-v53 .brc-kicker,
          .sr-result-card-v53 .brc-context,
          .sr-result-card-v53 [class*="kicker"],
          .sr-result-card-v53 [class*="context"] {
            max-width: calc(100% - 78px) !important;
            padding-right: 0 !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            font-size: 9.5px !important;
            line-height: 1 !important;
            letter-spacing: .14em !important;
          }

          .sr-result-card-v53 .brc-title,
          .sr-result-card-v53 h3,
          .sr-result-card-v53 h4,
          .sr-result-card-v53 strong {
            max-width: calc(100% - 76px) !important;
            padding-right: 0 !important;
            color: #0f172a !important;
            font-size: 14px !important;
            line-height: 1.05 !important;
            letter-spacing: -.04em !important;
            font-weight: 950 !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }

          .sr-result-card-v53 .brc-description,
          .sr-result-card-v53 p,
          .sr-result-card-v53 small {
            max-width: calc(100% - 12px) !important;
            color: #667085 !important;
            font-size: 11px !important;
            line-height: 1.15 !important;
            font-weight: 760 !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }

          .sr-result-card-v53 .sr-result-chip-hide-v53 {
            display: none !important;
          }

          .sr-result-card-v53 .sr-result-chip-wrap-v53 {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: wrap !important;
            align-items: center !important;
            gap: 4px !important;
            margin-top: 5px !important;
            max-width: 100% !important;
          }

          .sr-result-card-v53 .sr-result-chip-v53 {
            width: auto !important;
            min-width: 0 !important;
            max-width: 92px !important;
            height: 19px !important;
            min-height: 19px !important;
            max-height: 19px !important;
            padding: 0 7px !important;
            border-radius: 999px !important;
            border: 1px solid rgba(0, 103, 95, .12) !important;
            background: #f0fdfa !important;
            color: #00675f !important;
            box-shadow: none !important;

            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;

            font-size: 9.2px !important;
            line-height: 1 !important;
            font-weight: 950 !important;
            letter-spacing: -.005em !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }

          .sr-result-card-v53 .brc-actions,
          .sr-result-card-v53 [class*="actions"] {
            margin-top: 0 !important;
          }

          .sr-result-card-v53 img,
          .sr-result-card-v53 .brc-media,
          .sr-result-card-v53 .brc-logo,
          .sr-result-card-v53 .brc-thumb,
          .sr-result-card-v53 .brc-image {
            border-radius: 17px !important;
          }
        }

        @media (max-width: 390px) {
          .sr-result-card-v53 .sr-result-reserve-cta-v53 {
            top: 8px !important;
            right: 8px !important;
            min-width: 62px !important;
            max-width: 72px !important;
            height: 28px !important;
            min-height: 28px !important;
            max-height: 28px !important;
            padding: 0 9px !important;
            font-size: 10px !important;
          }

          .sr-result-card-v53 .brc-title,
          .sr-result-card-v53 h3,
          .sr-result-card-v53 h4,
          .sr-result-card-v53 strong {
            max-width: calc(100% - 70px) !important;
            font-size: 13.5px !important;
          }

          .sr-result-card-v53 .brc-kicker,
          .sr-result-card-v53 .brc-context,
          .sr-result-card-v53 [class*="kicker"],
          .sr-result-card-v53 [class*="context"] {
            max-width: calc(100% - 72px) !important;
          }

          .sr-result-card-v53 .sr-result-chip-v53 {
            max-width: 82px !important;
            height: 18px !important;
            min-height: 18px !important;
            max-height: 18px !important;
            padding: 0 6px !important;
            font-size: 8.9px !important;
          }
        }



        /* =========================================================
           LOOP SERVICE RESULTS — HEADER GAP FIT V57
           Runtime audit:
           - Header: fixed top 0 / bottom 58.
           - sr-pro-page started at 116, so the page was double-offset by
             one mobile-header height.
           Fix:
           - Pull only this route back by exactly one reserved header height.
           - Keep the content below the fixed header.
           - Do not touch Header.js or global body padding.
           ========================================================= */
        @media (max-width: 920px) {
          body:not(.is-mobile-app) .sr-pro-page {
            margin-top: calc(-1 * var(--loop-global-header-mobile-h, 58px)) !important;
          }

          body.is-mobile-app .sr-pro-page {
            margin-top: calc(-1 * var(--loop-mobile-header-total, var(--loop-native-header-h, var(--loop-global-header-mobile-h, 88px)))) !important;
          }

          .sr-pro-page .sr-shell {
            padding-top: 10px !important;
          }

          .sr-pro-page .sr-hero {
            padding-top: 0 !important;
          }
        }

        @media (max-width: 540px) {
          .sr-pro-page .sr-shell {
            padding-top: 8px !important;
          }
        }

`}</style>

      <main className="sr-shell sr-search-workspace search-workspace-v2">
        <div className="search-page-heading"><button type="button" onClick={() => navigate("/")}>{t("← Explorar")}</button><h1>{t("Encuentra tu próximo servicio")}</h1><p>{t("Compara negocios y descubre lo que ofrecen.")}</p></div>
        <MarketplaceSearch value={localSearch} onChange={setLocalSearch} onSearch={term => navigate(`/services/search?q=${encodeURIComponent(term)}`)} />
        <section className="sr-marketplace-top sr-compact-top sr-search-top-clean" aria-label={t("Resultados y filtros")}>
          <div className="sr-search-topline-clean">
            <button type="button" className="sr-back-btn sr-compact-back" onClick={() => navigate(-1)}>{t("← Volver")}</button>

            <div className="sr-search-status-pill" aria-label={t("Cantidad de resultados")}>
              <span>{resultCountText}</span>
            </div>
          </div>

          <div className="sr-marketplace-filter-row sr-filter-row-clean" aria-label={t("Filtros principales")}>
            <div className="sr-active-pills sr-active-query-pills" aria-label={t("Filtros activos")}>
              {activeFilterPills.filter(pill => !["scope", "sort"].includes(pill.key)).slice(0, 5).map((pill) => (
                <button key={`${pill.key}-${pill.label}`} type="button" onClick={() => removeSingleFilter(pill.key)}>
                  <span>{pill.label}</span>
                  <strong aria-hidden="true">×</strong>
                </button>
              ))}
            </div>

            {businesses.length > 0 || loading ? (
              <div className="sr-control-bar sr-marketplace-controls sr-controls-clean">
                <button
                  type="button"
                  ref={filterBtnRef}
                  className={`sr-control-btn sr-filter-icon-btn ${activeFilterCount ? "is-active" : ""}`}
                  onClick={() => openFilterTab("keyword")}
                  aria-label={t("Abrir filtros")}
                >
                  <span className="sr-filter-sliders" aria-hidden="true">
                    <i></i><i></i><i></i>
                  </span>
                  <span>Filtros</span>
                  {activeFilterCount ? <strong>{activeFilterCount}</strong> : null}
                </button>

                <select className="search-order-select" aria-label={t("Ordenar resultados")} value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  <option value="recommended">{t("Relevancia")}</option><option value="rating">{t("Mejor valorados")}</option><option value="new">{t("Más recientes")}</option><option value="location">{t("Ubicación")}</option>
                </select>
              </div>
            ) : null}
          </div>


        </section>

        {filterSheetOpen && typeof document !== "undefined" ? ReactDOM.createPortal((
          <div className="sr-filter-sheet sr-filter-sheet-e24 sr-filter-sheet-portal-hardfix" role="dialog" aria-modal="true" aria-label={t("Filtros de resultados")}>
            <button type="button" className="sr-filter-sheet-backdrop" onClick={() => setFilterSheetOpen(false)} aria-label={t("Cerrar filtros")} />
            <div className="sr-filter-sheet-panel">
              <div className="sr-filter-sheet-head sr-e24-head">
                <div>
                  <span>Filtros</span>
                  <strong>{t("Refinar búsqueda")}</strong>
                </div>
                <button type="button" onClick={() => setFilterSheetOpen(false)}>×</button>
              </div>

              <div className="sr-e24-layout">
                <aside className="sr-e24-sidebar" aria-label={t("Secciones de filtros")}>
                  {[
                    ["keyword", "Palabra clave", Boolean(paramRaw)],
                    ["category", "Categoría", Boolean(paramRaw)],
                    ["service", "Servicio", Boolean(selectedSubservice)],
                    ["location", "Ubicación", Boolean(selectedLocation)],
                    ["modality", "Modalidad", Boolean(filters.onsite || filters.home || filters.online)],
                  ].map(([key, label, active]) => (
                    <button
                      key={key}
                      type="button"
                      className={`${filterTab === key ? "is-current" : ""} ${active ? "has-check" : ""}`}
                      onClick={() => setFilterTab(key)}
                    >
                      <span>{label}</span>
                      {active ? <strong>✓</strong> : null}
                    </button>
                  ))}
                </aside>

                <div className="sr-e24-content sr-e24-content-grid">
                  <section className={`sr-e24-section sr-e24-card span-2 ${filterTab === "keyword" ? "is-focus" : ""}`}>
                    <div className="sr-e24-section-head">
                      <div>
                        <h3>{t("Palabra clave")}</h3>
                        <p>{t("Servicio, negocio o zona.")}</p>
                      </div>
                      {paramRaw ? <span>✓</span> : null}
                    </div>
                    <div className="sr-e24-input">
                      <span aria-hidden="true">⌕</span>
                      <input
                        value={localSearch}
                        onChange={(e) => setLocalSearch(e.target.value)}
                        placeholder={t("Ej: uñas, barbería, masaje...")}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            applyKeywordSearch();
                          }
                        }}
                      />
                      {localSearch ? <button type="button" onClick={() => setLocalSearch("")}>×</button> : null}
                    </div>
                  </section>

                  <section className={`sr-e24-section sr-e24-card span-2 ${filterTab === "category" ? "is-focus" : ""}`}>
                    <div className="sr-e24-section-head">
                      <div>
                        <h3>{t("Categoría")}</h3>
                        <p>{t("Cambia el tipo principal.")}</p>
                      </div>
                      {paramRaw ? <span>✓</span> : null}
                    </div>
                    <div className="sr-e24-chip-grid compact">
                      {categoryOptions.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          className={String(cat.id) === String(activeServiceId) ? "active" : ""}
                          onClick={() => {
                            if (isEventsCategory(cat.raw)) navigate("/events");
                            else navigate(`/services/${encodeURIComponent(cat.id)}`);
                            setFilterSheetOpen(false);
                          }}
                        >
                          {t(cat.title)}
                        </button>
                      ))}
                    </div>
                  </section>

                  <section className={`sr-e24-section sr-e24-card span-2 ${filterTab === "location" ? "is-focus" : ""}`}>
                    <div className="sr-e24-section-head">
                      <div>
                        <h3>{t("Ubicación")}</h3>
                        <p>{t("Busca zona, corregimiento, distrito o provincia.")}</p>
                      </div>
                      {selectedLocation ? <span>✓</span> : null}
                    </div>
                    <div className="sr-e24-input">
                      <span aria-hidden="true">⌖</span>
                      <input
                        value={locationFilterSearch}
                        onChange={(e) => setLocationFilterSearch(e.target.value)}
                        placeholder={t("Ej: Costa del Este, San Francisco...")}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="none"
                        spellCheck={false}
                      />
                      {locationFilterSearch ? <button type="button" onClick={() => setLocationFilterSearch("")}>×</button> : null}
                    </div>

                    {selectedLocation ? (
                      <div className="sr-e24-selected-location">
                        <span>{t("Seleccionada")}</span>
                        <button type="button" onClick={() => setSelectedLocation(null)}>{selectedLocation} ×</button>
                      </div>
                    ) : null}

                    {locationFilterSearch.trim() ? (
                      <div className="sr-e24-location-results">
                        {filteredLocationOptions.length ? filteredLocationOptions.map((loc) => (
                          <button
                            key={loc}
                            type="button"
                            className={selectedLocation === loc ? "active" : ""}
                            onClick={() => {
                              setSelectedLocation(loc);
                              setLocationFilterSearch("");
                            }}
                          >
                            {loc}
                          </button>
                        )) : <em>{t("No encontramos zonas con ese texto.")}</em>}
                      </div>
                    ) : null}
                  </section>

                  <section className={`sr-e24-section sr-e24-card span-2 ${filterTab === "service" ? "is-focus" : ""}`}>
                    <div className="sr-e24-section-head">
                      <div>
                        <h3>Servicio</h3>
                        <p>{t("Servicios detectados en los negocios disponibles.")}</p>
                      </div>
                      {selectedSubservice ? <span>✓</span> : null}
                    </div>
                    <div className="sr-e24-chip-grid compact">
                      {subservices.length === 0 ? (
                        <em>{t("No hay servicios detectados todavía.")}</em>
                      ) : (
                        subservices.slice(0, 28).map((s) => (
                          <button
                            key={s}
                            type="button"
                            className={selectedSubservice === s ? "active" : ""}
                            onClick={() => setSelectedSubservice(selectedSubservice === s ? null : s)}
                          >
                            {s}
                          </button>
                        ))
                      )}
                    </div>
                  </section>

                  <section className={`sr-e24-section sr-e24-card span-2 ${filterTab === "modality" ? "is-focus" : ""}`}>
                    <div className="sr-e24-section-head">
                      <div>
                        <h3>{t("Modalidad")}</h3>
                        <p>{t("Cómo quieres recibir el servicio.")}</p>
                      </div>
                      {filters.onsite || filters.home || filters.online ? <span>✓</span> : null}
                    </div>
                    <div className="sr-e24-chip-grid compact service-like">
                      <button type="button" className={filters.onsite ? "active" : ""} onClick={() => toggleFilter("onsite")}>En local</button>
                      <button type="button" className={filters.home ? "active" : ""} onClick={() => toggleFilter("home")}>{t("A domicilio")}</button>
                      <button type="button" className={filters.online ? "active" : ""} onClick={() => toggleFilter("online")}>Online</button>
                    </div>
                  </section>

                </div>
              </div>

              <div className="sr-filter-sheet-actions sr-e24-actions">
                <button type="button" className="secondary" onClick={resetPremiumFilters}>{t("Limpiar")}</button>
                <button type="button" className="primary" onClick={() => setFilterSheetOpen(false)}>{t("Ver resultados (")}{processed.length})</button>
              </div>
            </div>
          </div>
        ), document.body) : null}

        {featuredResults.length > 0 ? (
          <section className="sr-featured-strip" aria-label={t("Resultados destacados")}>
            <div className="sr-featured-head">
              <span>Destacados</span>
              <strong>{t("Mejores opciones para empezar")}</strong>
            </div>
            <div className="sr-featured-grid sr-featured-card-grid">
              {featuredResults.map((b) => (
                <BusinessResultCard
                  key={`featured-${b.id}`}
                  biz={b}
                  variant="featured"
                  priority
                  contextLabel="Destacado"
                  actionLabel="Ver servicios"
                  onOpenBooking={() => navigate(`/business/${b.id}`)}
                  onOpenBusiness={() => navigate(`/business/${b.id}`)}
                />
              ))}
            </div>
          </section>
        ) : null}

        {false && businesses.length > 0 && subservices.length > 0 ? (
          <section className="sr-subservice-strip" aria-label={t("Subservicios disponibles")}>
            <button
              type="button"
              onClick={() => setSelectedSubservice(null)}
              className={`sr-subservice-pill ${!selectedSubservice ? "is-active" : ""}`}
            >{t("Todos")}</button>
            {subservices.slice(0, 14).map((s) => {
              const active = selectedSubservice === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSelectedSubservice(active ? null : s)}
                  className={`sr-subservice-pill ${active ? "is-active" : ""}`}
                >
                  {s}
                </button>
              );
            })}
          </section>
        ) : null}

        <section className="sr-results-headline" aria-label={t("Resumen de resultados")}>
          <div>

            <strong>{loading ? "Buscando opciones…" : `${processed.length} negocio${processed.length === 1 ? "" : "s"}${isSearchMode && paramRaw ? ` para “${paramRaw}”` : ""}`}</strong>
          </div>
          {(anyFilterOn || sortBy !== "recommended") ? (
            <button type="button" onClick={resetPremiumFilters}>{t("Limpiar filtros")}</button>
          ) : null}
        </section>

        <section className="sr-content-grid">
          <div className="sr-main-list">
            {loading && businesses.length === 0 ? (
              <div className="sr-loading-card" style={{ display: "grid", gap: 12 }}>
                {[0, 1, 2].map((i) => <div key={i} className="sr-skeleton-line" />)}
              </div>
            ) : null}

            {!loading && !error && processed.length === 0 ? (
              <SmartResultsEmptyState
                title={activeTitle}
                searchQuery={isSearchMode ? paramRaw : ""}
                hasBaseResults={businesses.length > 0}
                activeFilterCount={activeFilterCount}
                relatedChips={relatedEmptyChips}
                onClearFilters={resetPremiumFilters}
                onExploreAll={() => navigate("/services/search?q=servicios")}
                onEarlyAccess={() => navigate(getEarlyAccessPath())}
                onChip={handleSmartChip}
              />
            ) : null}

            {processed.map((b, index) => (
              <BusinessResultCard
                key={b.id}
                biz={b}
                variant="list"
                priority={index < 4}
                contextLabel={b.businessTypeLabel || ""}
                actionLabel="Ver servicios"
                onOpenBooking={() => navigate(`/business/${b.id}`)}
                onOpenBusiness={() => navigate(`/business/${b.id}`)}
              />
            ))}
          </div>

          <aside className="sr-side-panel" aria-label={t("Subservicios")}>
            <div className="sr-side-title">{t("Subservicios")}</div>
            <div className="sr-subservices">
              {subservices.length === 0 ? (
                <span style={{ color: "#94a3b8", fontSize: 13, fontWeight: 750 }}>{t("No hay subservicios")}</span>
              ) : (
                subservices.map((s) => {
                  const active = selectedSubservice === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSelectedSubservice(active ? null : s)}
                      className={`sr-subservice-chip ${active ? "is-active" : ""}`}
                    >
                      {s}
                    </button>
                  );
                })
              )}
            </div>
          </aside>
        </section>

        <div className="sr-load-more">
          {hasMore && lastVisibleRef.current ? (
            <button type="button" onClick={loadMore}>{t("Cargar más")}</button>
          ) : null}
        </div>
      </main>
    </div>
  );
}
